import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { TrailerButton } from './ReleaseStatus';
import axios from 'axios';
import { minutesLeft } from '../lib/format';
import {
  AUDIO_LANGUAGES,
  audioLanguageLabel,
  autoOrder,
  reportVerdict,
  runtimeRulesOut,
  labelFor,
  preferredAudio,
  qualityTiers,
  routeFor,
  searchReleases,
  startBest,
  startRelease,
  trackIsLanguage,
  trackLanguageCode
} from '../lib/playback';
import { isTV } from '../lib/tv';
import { haptic, isIOS, useTouchUI } from '../lib/touch';
import Sheet from './Sheet';

/**
 * Whether a remux has to come as HLS rather than the piped MP4.
 *
 * Safari's player (every browser on an iPhone, and Safari on a Mac) opens a
 * video with a range request and refuses an answer with no length, which is
 * all a remux generated as it is sent can give - so on phones, every release
 * that needed ffmpeg spun forever. Android's player stalled the same way. Both
 * play HLS natively. Desktop Chrome and Firefox keep the piped MP4, which they
 * play fine and which starts sooner; the TV app's WebView does too, since it
 * already works and has no reason to change.
 */
const REMUX_AS_HLS = (() => {
  if (isTV || typeof document === 'undefined') return false;
  const ua = navigator.userAgent;
  const native = document.createElement('video').canPlayType('application/vnd.apple.mpegurl');
  const appleWebKit = /iPhone|iPad|iPod/.test(ua) ||
    (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) ||
    (/Safari\//.test(ua) && !/Chrome|Chromium|CriOS|Edg|OPR|Android/.test(ua));
  return Boolean(native) && (appleWebKit || /Android/.test(ua));
})();

const REPORT_EVERY_MS = 15_000;
const SKIP_SECONDS = 10;
const IDLE_MS = 2600;
// A phone has no pointer to move, so the controls are shown by a tap and
// stay a little longer than a mouse's: reaching for a button takes a moment
const IDLE_TOUCH_MS = 4000;
// Two taps on the same side within this long are a double tap (a seek); a
// tap on that side shortly after a seek seeks again, YouTube's way
const DOUBLE_TAP_MS = 280;
const SEEK_STREAK_MS = 700;
// A finger has to travel this far before a press becomes a swipe
const SWIPE_SLOP = 12;
// Pulled down this far, or flicked, the player closes
const CLOSE_PULL_PX = 120;
// Swipes that begin this close to a side edge belong to the browser - Safari's
// back gesture lives there
const EDGE_PX = 24;

// Mid-film deaths of a transcoded stream survived in a row without the film
// advancing. Bounded so a source that is truly gone reaches the error screen
// instead of restarting forever.
const STALL_RETRIES = 3;

// How near the probed runtime a transcoded stream has to end for it to count as
// the film ending rather than the stream ending. Video is copied, so a segment
// begins at the keyframe at or before where it was asked for, and the reported
// position can sit a few seconds behind the picture; this is comfortably wider
// than that, because restarting a film that has genuinely finished is the worse
// of the two mistakes.
const ENDED_SLACK_S = 20;

// How long an episode has to have been playing before the next one is warmed.
// Long enough that this episode has its own buffer and the viewer has stayed.
const PREFETCH_AFTER_MS = 90_000;

// When the standing "Next episode" button appears, in seconds before the end.
// There are no credit markers to key off — releases of the same episode don't
// even end at the same second — so this is a guess sized to credits, and sized
// to err short: a button over the last minute is ignorable, a card over the
// last scene is not.
const NEXT_OFFER_SECONDS = 60;

// How long the end card counts down before playing the next episode on its
// own. Long enough to cancel from across the room, short enough that a binge
// never feels parked on a menu.
const COUNTDOWN_SECONDS = 8;

// One nudge of the caption timing. 100ms is about the smallest shift that is
// actually perceptible against speech, so a smaller step would just mean more
// key presses to fix the same drift.
const SYNC_STEP = 0.1;
const SYNC_LIMIT = 30;

// Remembered across films: someone who watches with English captions wants
// them on the next one too, and someone who turned them off meant it.
const PREF = { language: 'captions.language', enabled: 'captions.enabled' };

// The subtitle sources are public and flaky; a search that fails or comes
// back empty on a degraded round is asked again, briefly later, before the
// menu concludes there is nothing.
const CC_SEARCH_RETRIES = 2;
const CC_RETRY_MS = 3000;

const clock = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const whole = Math.floor(seconds);
  const s = String(whole % 60).padStart(2, '0');
  const m = Math.floor(whole / 60) % 60;
  const h = Math.floor(whole / 3600);
  return h ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
};

const Icon = ({ d, size = 20 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
    <path d={d} />
  </svg>
);

const PATH = {
  play: 'M8 5.2c0-.8.9-1.3 1.6-.9l9.1 5.9c.6.4.6 1.3 0 1.7l-9.1 5.9c-.7.4-1.6-.1-1.6-.9V5.2z',
  pause: 'M7 4.6h3.1v14.8H7V4.6zm6.9 0H17v14.8h-3.1V4.6z',
  back10: 'M12 5V2.5L8.2 6 12 9.5V7a5.5 5.5 0 1 1-5.5 5.5H4.5A7.5 7.5 0 1 0 12 5z',
  fwd10: 'M12 5V2.5L15.8 6 12 9.5V7a5.5 5.5 0 1 0 5.5 5.5h2A7.5 7.5 0 1 1 12 5z',
  volume: 'M4 9.5h3.3L11.5 6v12L7.3 14.5H4v-5zm10.6-.9a4.2 4.2 0 0 1 0 6.8l-1-1.4a2.5 2.5 0 0 0 0-4l1-1.4zm2.1-2.8a7.7 7.7 0 0 1 0 12.4l-1-1.4a6 6 0 0 0 0-9.6l1-1.4z',
  muted: 'M4 9.5h3.3L11.5 6v12L7.3 14.5H4v-5zm11.9 2.5 2.6-2.6-1.2-1.2-2.6 2.6-2.6-2.6-1.2 1.2 2.6 2.6-2.6 2.6 1.2 1.2 2.6-2.6 2.6 2.6 1.2-1.2-2.6-2.6z',
  enterFull: 'M4 9V4h5v2H6v3H4zm11-5h5v5h-2V6h-3V4zM4 15h2v3h3v2H4v-5zm14 0h2v5h-5v-2h3v-3z',
  exitFull: 'M9 4h2v5H6V7h3V4zm4 0h2v3h3v2h-5V4zM6 15h5v5H9v-3H6v-2zm7 0h5v2h-3v3h-2v-5z',
  cc: 'M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zM4 12h4v2H4v-2zm10 6H4v-2h10v2zm6 0h-4v-2h4v2zm0-4H10v-2h10v2z',
  audio: 'M12 3a4 4 0 0 1 4 4v4a4 4 0 0 1-8 0V7a4 4 0 0 1 4-4zM6 11h1.8a4.2 4.2 0 0 0 8.4 0H18a6 6 0 0 1-5 5.9V20h-2v-3.1A6 6 0 0 1 6 11z',
  // Sliders: quality is a setting you move between, not a device you toggle
  quality: 'M4 6h9v2H4V6zm11 0h5v2h-5V6zm-4 5h9v2h-9v-2zM4 11h5v2H4v-2zm0 5h9v2H4v-2zm11 0h5v2h-5v-2zM12 4h2v6h-2V4zM8 9h2v6H8V9zm6 5h2v6h-2v-6z',
  close: 'M5.6 8.3 12 14.7l6.4-6.4 1.4 1.4-7.8 7.8-7.8-7.8 1.4-1.4z',
  episodes: 'M3 5h2v2H3V5zm4 0h14v2H7V5zM3 11h2v2H3v-2zm4 0h14v2H7v-2zm-4 6h2v2H3v-2zm4 0h14v2H7v-2z',
  brightness: 'M12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10zm-1-6h2v3h-2V1zm0 19h2v3h-2v-3zM1 11h3v2H1v-2zm19 0h3v2h-3v-2zM4.2 5.6l1.4-1.4 2.1 2.1-1.4 1.4-2.1-2.1zm12.1 12.1 1.4-1.4 2.1 2.1-1.4 1.4-2.1-2.1zM4.2 18.4l2.1-2.1 1.4 1.4-2.1 2.1-1.4-1.4zM16.3 6.3l2.1-2.1 1.4 1.4-2.1 2.1-1.4-1.4z',
  // A screen with waves out of the corner: the shape every casting phone
  // already taught the household to look for
  cast: 'M21 3H3c-1.1 0-2 .9-2 2v3h2V5h18v14h-7v2h7c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM1 18v3h3c0-1.66-1.34-3-3-3zm0-4v2c2.76 0 5 2.24 5 5h2c0-3.87-3.13-7-7-7zm0-4v2c4.97 0 9 4.03 9 9h2c0-6.08-4.92-11-11-11z'
};

/**
 * Hang a media token off a stream URL. The <video> element — and anything the
 * URL gets handed to, which is the whole point — cannot send headers or
 * cookies, so the way in rides the query string.
 */
const withToken = (url, token) => {
  if (!url || !token) return url;
  return `${url}${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
};

// How the language codes arrive from the container: ISO 639-2, three letters.
const LANGUAGE_NAMES = new Intl.DisplayNames(['en'], { type: 'language' });

const languageOf = (code) => {
  if (!code || code === 'und') return null;
  try {
    return LANGUAGE_NAMES.of(code) || null;
  } catch {
    // Not every three-letter tag in the wild is a real one
    return null;
  }
};

/**
 * Name an audio track the way someone choosing between them would.
 *
 * Language first because that is what a second track usually differs by, then
 * the embedded title - which is where "Commentary" lives - then the codec and
 * channel count to tell two same-language tracks apart.
 */
const audioLabel = (entry) => {
  const parts = [languageOf(entry.language), entry.title].filter(Boolean);
  const channels = entry.channels >= 6 ? '5.1' : entry.channels === 2 ? 'Stereo' : null;
  const technical = [entry.codec?.toUpperCase(), channels].filter(Boolean).join(' ');

  if (!parts.length) return technical || 'Track';
  return `${parts.join(' · ')}${technical ? ` — ${technical}` : ''}`;
};

const syncLabel = (offset) => {
  if (Math.abs(offset) < 0.05) return 'in sync';
  return `${offset > 0 ? '+' : '−'}${Math.abs(offset).toFixed(1)}s`;
};

/** What the automatic sync has to say for itself, in the captions menu. */
const autoLabel = (auto) => {
  switch (auto.status) {
    case 'running':
      return 'Listening to the film to line the captions up…';
    case 'done':
      return `Auto-synced ${auto.offset > 0 ? '+' : '−'}${Math.abs(auto.offset).toFixed(2)}s${
        auto.scale !== 1 ? ' and corrected the frame rate' : ''
      } · match ${Math.round(auto.confidence * 100)}%`;
    case 'weak':
      return 'Could not match these captions to the speech - they may be for a different cut. Try another file, or nudge by hand.';
    case 'failed':
      return 'Auto sync could not read this stream. Nudge by hand.';
    case 'unavailable':
      return 'Auto sync needs ffmpeg on the server.';
    default:
      return '';
  }
};

// WebVTT allows inline markup - <i>, <c.classname>, <00:00:01.000> karaoke
// timestamps. The overlay draws plain text, so the tags come out rather than
// being shown literally.
//
// Entity-encoded tags come out too: cue.text is the *raw* payload, so a file
// whose converter escaped an SRT styling tag hands this "&lt;font ...&gt;",
// which the bare tag-strip does not match and the overlay then paints
// verbatim over the picture. The server no longer writes those, but browsers
// cache VTT files for hours, so the decode has to live here as well - angle
// entities first, then one strip catches written and encoded tags alike, and
// the leftover harmless entities become the characters they mean.
const plain = (text) =>
  text
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/<[^>]*>/g, '')
    .replace(/&amp;/gi, '&')
    .replace(/&(?:quot|#0?34);/gi, '"')
    .replace(/&(?:apos|#0?39);/gi, "'")
    .replace(/&nbsp;/gi, ' ')
    .trim();

// Six figures is common here, and the exact number is never the point - only
// the order of magnitude next to the neighbouring row.
const downloads = (count) => {
  if (!count) return '';
  if (count < 1000) return String(count);
  return `${(count / 1000).toFixed(count < 10_000 ? 1 : 0)}k`;
};

/**
 * Playback for both routes into it: a file inside a torrent this server is
 * seeding (/player/:torrentId/:fileId) and a debrid link proxied through it
 * (/watch?src=…). Either way the source is a path on this origin, so the
 * <video> element carries the session cookie without being asked - which is
 * just as well, since it cannot set an Authorization header.
 *
 * The native controls are replaced wholesale: they can't show how far the
 * stream has buffered ahead, which is the one thing worth knowing when the
 * bytes are arriving from a debrid host over someone else's link.
 */
const Player = () => {
  const { torrentId, fileId } = useParams();
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  // A phone gets its own controls: see the touch layer at the bottom
  const touchUI = useTouchUI();

  const shellRef = useRef(null);
  const videoRef = useRef(null);
  const trackRef = useRef(null);
  const ccRef = useRef(null);
  // How much of `offset` the loaded cues have already been shifted by. Cue
  // times are mutated in place, so what gets applied is always the difference.
  const shifted = useRef(0);
  const lastReport = useRef(0);
  const idleTimer = useRef(0);
  const flashTimer = useRef(0);
  // True between a transcode seek and the replacement stream playing, while the
  // element is expected to report an error about the response we just abandoned
  const swapping = useRef(false);
  // Transcoded streams that have ended mid-film in a row without the film
  // advancing, spent against STALL_RETRIES by `resumeAfterStall`
  const stallRetries = useRef(0);
  // Where to land once a swapped-in stream is ready, in film seconds. Changing
  // quality mid-film means starting a different release, and nobody means "start
  // this again from the beginning" by it.
  //
  // Continue-watching seeds it the same way: the rail re-finds a release and
  // hands over the position it remembered, and this is the machinery that was
  // already there for landing a fresh stream somewhere other than zero.
  const pendingSeek = useRef(
    Number.isFinite(location.state?.resumeAt) && location.state.resumeAt > 5
      ? location.state.resumeAt
      : null
  );

  const [streamUrl, setStreamUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  /**
   * The grant that lets cookie-less fetchers play this stream — a Chromecast
   * or Apple TV handed the URL by the cast button below. Fetched before the
   * first frame rather than when casting starts, because it becomes part of
   * the src: appending it later would swap the src and restart playback at
   * the worst possible moment.
   *
   * Failing to get one is not fatal — the browser itself still authenticates
   * with its cookie — it only means the cast button stays hidden.
   */
  const [mediaToken, setMediaToken] = useState(null);
  const [tokenSettled, setTokenSettled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get('/api/auth/media-token')
      .then(({ data }) => {
        if (!cancelled) setMediaToken(data.data.token);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setTokenSettled(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const [playing, setPlaying] = useState(false);
  const [waiting, setWaiting] = useState(true);
  // Whether a first frame has ever played. Until it has, the title's own
  // artwork holds the screen — opening a stream takes long enough that a black
  // void here reads as the film's cover "disappearing" rather than as loading.
  const [started, setStarted] = useState(false);
  const [current, setCurrent] = useState(0);
  // What the <video> itself reports. Authoritative when playing direct; when
  // transcoding it describes only the segment being served, so `duration`
  // below prefers the probed length.
  const [mediaDuration, setMediaDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  const [scrubbing, setScrubbing] = useState(false);
  const [hover, setHover] = useState(null); // { ratio, x } while pointing at the track
  const [idle, setIdle] = useState(false);
  const [flash, setFlash] = useState(null); // 'back' | 'fwd', the ±10s pulse

  const [tracks, setTracks] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [trackId, setTrackId] = useState('');
  const [captions, setCaptions] = useState('idle'); // idle|loading|ready|none|error
  const [ccOn, setCcOn] = useState(() => localStorage.getItem(PREF.enabled) !== 'off');
  const [offset, setOffset] = useState(0);
  // What automatic sync found for the loaded track against this file: the
  // shift and the time scale that put the cues on the speech. The manual nudge
  // above rides on top of it. `run` bumps to ask again.
  const [ccAuto, setCcAuto] = useState({ status: 'idle', offset: 0, scale: 1, confidence: 0 });
  const [ccAutoRun, setCcAutoRun] = useState(0);
  const [cue, setCue] = useState('');
  const [dead, setDead] = useState(false); // every track in the language would not load
  // Tracks that errored this session, so the automatic fallback never loops
  // back onto one; a ref mirrors it for the error handler's benefit.
  const [failed, setFailed] = useState([]);
  const failedRef = useRef([]);
  failedRef.current = failed;

  // What the container actually holds, read by ffprobe rather than guessed from
  // the release name: { needsTranscode, reencode, best, audio[], duration }.
  const [probe, setProbe] = useState(null);
  // The browser refused the source outright. Not fatal on its own - ffmpeg can
  // usually remux it into something playable - so this asks for that rather
  // than ending the film. See the <video> onError for the whole story.
  const [directFailed, setDirectFailed] = useState(false);
  // Where the currently-served transcode segment starts, in film seconds. Always
  // 0 when playing direct. See `seekAbsolute` for why this has to exist at all.
  const [base, setBase] = useState(0);
  const [audioOrdinal, setAudioOrdinal] = useState(null);

  // Quality, audio and captions all open into the same corner above the
  // controls, so at most one of them is open: this is which.
  const [menu, setMenu] = useState(null); // null | 'quality' | 'audio' | 'cc'
  const toggleMenu = useCallback((which) => setMenu((open) => (open === which ? null : which)), []);
  const closeMenu = useCallback(() => setMenu(null), []);
  const qualityMenu = menu === 'quality';
  const audioMenu = menu === 'audio';
  const ccMenu = menu === 'cc';

  const label = location.state || {};
  const directSrc = params.get('src');

  /**
   * The audio language this stream is in, as asked for: `original` or an ISO
   * 639-1 code. Seeded from how the stream was started (the title page, the
   * rail, the previous episode), falling back to the profile's default, and
   * changed from the audio menu. Every search the player runs - the quality
   * menu, the next episode, the resume entry - asks in this language, so a
   * switch to French holds for the rest of the evening's binge.
   */
  const [streamLang, setStreamLang] = useState(() => label.audioLanguage || preferredAudio());
  const [langSwitching, setLangSwitching] = useState(null);
  const [langNote, setLangNote] = useState('');
  const [langMore, setLangMore] = useState(false);

  // The ISO 639-1 language `streamLang` resolves to for this title. Original
  // means the work's own language, which the catalog may not know - null then.
  const langCode = (lang) => (lang === 'original' ? label.originalLanguage || null : lang);

  /**
   * Which release is on screen — a debrid link, or a file in a torrent here.
   *
   * The route only seeds this. Changing quality replaces it in place rather than
   * navigating, because navigating would tear down the element, the probe, the
   * caption search and the release list, all of which are still correct.
   */
  const [origin, setOrigin] = useState(() =>
    directSrc ? { src: directSrc } : { torrentId, fileId }
  );
  const [swapError, setSwapError] = useState('');
  // The cam warning is worth saying once, not worth saying over a film someone
  // has decided to watch anyway.
  const [camAcknowledged, setCamAcknowledged] = useState(false);

  useEffect(() => {
    if (origin.src) {
      setStreamUrl(origin.src);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    axios
      .get(`/api/stream/url/${origin.torrentId}/${origin.fileId}`)
      .then(({ data }) => {
        if (!cancelled) setStreamUrl(data.data.url);
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.error || 'Failed to load stream');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [origin]);

  /* ----- audio ---------------------------------------------------------- */

  /**
   * Ask the server what the container actually holds.
   *
   * Most releases here carry EAC3, AC3, DTS or TrueHD, and no browser decodes
   * any of those: the picture plays and the film is silent, with nothing on
   * screen explaining why. The server reads the real stream table, so it also
   * spots the stereo AAC track a release name never mentioned - which is worth
   * far more than transcoding, because selecting it costs nothing.
   *
   * Failure is not fatal anywhere in here. No ffmpeg on the host, an
   * unreadable container, a request that errors - all of them fall through to
   * playing direct, which is exactly what this did before any of it existed.
   */
  useEffect(() => {
    if (!streamUrl) return undefined;

    let cancelled = false;
    setProbe(null);
    setBase(0);
    setAudioOrdinal(null);
    // A different source gets its own chance to play directly
    setDirectFailed(false);

    axios
      .get(`/api/transcode/inspect?src=${encodeURIComponent(streamUrl)}`)
      .then(({ data }) => {
        if (!cancelled) setProbe(data.data);
      })
      .catch(() => {
        if (!cancelled) setProbe({ available: false, needsTranscode: false });
      });

    return () => {
      cancelled = true;
    };
  }, [streamUrl]);

  // Which audio track is playing: the server's pick unless the viewer overrode it
  const activeAudio = audioOrdinal ?? probe?.best ?? 0;
  const audioTracks = probe?.audio || [];
  const chosenTrack = audioTracks.find((entry) => entry.ordinal === activeAudio) || null;

  /**
   * Land on the track in the stream's language, when the file has one and it
   * is not the one the server picked.
   *
   * A MULTI release carries several tracks, and which plays by default is the
   * file's decision - often the dub, since MULTI packs come from the dubbing
   * scenes. Before this, a French default on an English film played French
   * until someone found the audio menu. Now the track that matches what was
   * asked for wins, original included.
   *
   * Declared ahead of the effect that applies `pendingSeek`, and consumes it:
   * selecting a track means transcoding, where position is part of the
   * request, so the seek has to go into `base` here or the stream restarts at
   * zero.
   */
  useEffect(() => {
    if (!probe?.audio?.length || probe.audio.length < 2 || audioOrdinal !== null) return;

    const want = langCode(streamLang);
    if (!want) return;

    const best = probe.audio.find((entry) => entry.ordinal === probe.best);
    if (best && trackIsLanguage(best, want)) return;

    const match = probe.audio.find(
      (entry) => trackIsLanguage(entry, want) && !/commentary/i.test(entry.title || '')
    );
    if (!match) return;

    const at = pendingSeek.current ?? videoRef.current?.currentTime ?? 0;
    pendingSeek.current = null;
    swapping.current = true;
    setBase(at);
    setCurrent(at);
    setAudioOrdinal(match.ordinal);
    // Keyed on the probe only: re-running on a language change would fight a
    // track the viewer then picks by hand. A language change that lands on a
    // new file gets a new probe, and with it a fresh pass through here.
  }, [probe]);

  /**
   * Why the audio could not be checked, when it could not be.
   *
   * Only set on the two paths that give up - no ffmpeg on the host, or a
   * container that would not read - and both end the same way: playing direct,
   * which is silence on any release the browser cannot decode. Left unsaid, that
   * is a film with no sound and nothing anywhere admitting why.
   */
  const audioNote = probe?.reason || null;

  /**
   * Whether playback has to go through ffmpeg.
   *
   * Either the browser would be silent on its own, or the viewer asked for a
   * track other than the one it would have picked - reaching a specific track
   * means selecting it with -map, which is the transcoder's job either way.
   *
   * `directFailed` is the third way in, and it exists because the probe is not
   * always back in time to be asked. The element is pointed at the source the
   * moment there is one, and a container the browser cannot open - an AVI, say -
   * fails within a second, while reading the stream table out of that same file
   * over a debrid link takes several. So the answer arrives after the failure it
   * would have prevented. Treating the failure as a request to remux is what
   * lets the right answer still be acted on when it does arrive.
   */
  const transcoding = Boolean(
    probe?.available &&
      (probe.needsTranscode ||
        directFailed ||
        (audioOrdinal !== null && audioOrdinal !== probe.best))
  );

  /**
   * What to say when nothing could be made to play.
   *
   * "Stopped responding" is the right sentence for a stream that died mid-film
   * and the wrong one for a release that was never going to start: it sends
   * whoever reads it to look at their connection, when the answer is in the
   * file. These two are the reasons a remux cannot rescue one.
   */
  const deadEnd = useCallback(() => {
    if (probe && !probe.available) {
      return 'This release is in a format the browser cannot open, and this server has no ffmpeg to convert it.';
    }
    if (probe?.video && !probe.video.copyable) {
      return `This release's video is ${probe.video.codec}, which the browser cannot decode and this server will not re-encode.`;
    }
    return 'This stream stopped responding';
  }, [probe]);

  /**
   * Direct playback failed, and the probe has since come back saying there is no
   * ffmpeg to rescue it. Nothing else will happen on its own - the source never
   * changes, so no second error is coming - so the waiting ends here instead of
   * spinning forever.
   */
  useEffect(() => {
    if (directFailed && probe && !probe.available) setError(deadEnd());
  }, [directFailed, probe, deadEnd]);

  const playSrc = useMemo(() => {
    if (!transcoding) return withToken(streamUrl, mediaToken);

    const query = new URLSearchParams({
      src: streamUrl,
      t: String(base),
      a: String(activeAudio)
    });
    // A track MP4 can carry as-is only needs selecting, not re-encoding, and the
    // server is told so rather than probing a second time per seek. This asks
    // `copyable`, not `playable`: they part company on a track the browser would
    // have played where it was but that MP4 cannot hold, which is precisely the
    // case a remux now reaches.
    if (chosenTrack?.copyable) query.set('copy', '1');
    // The outer URL, not the inner src: the server stamps its own token onto
    // what it hands ffmpeg, but a cast device fetching this URL has only this.
    if (mediaToken) query.set('token', mediaToken);

    if (REMUX_AS_HLS) {
      // H.264 travels as MPEG-TS, anything else as fMP4; the server picks by this
      if (probe?.video?.codec) query.set('v', probe.video.codec);
      return `/api/transcode/hls?${query}`;
    }
    return `/api/transcode/stream?${query}`;
  }, [transcoding, streamUrl, base, activeAudio, chosenTrack, mediaToken, probe]);

  /**
   * A transcoded response is generated as it is sent, so it carries no duration
   * and no byte ranges. The probed length is the only thing that knows how long
   * the film is, and the scrub bar is drawn from it.
   */
  const duration = transcoding ? probe?.duration || 0 : mediaDuration;

  /* ----- quality -------------------------------------------------------- */

  /**
   * The releases this title is available in, fetched here rather than handed
   * over, so the list is still there on a reload and is never staler than the
   * moment the menu is opened. It is the same ranked search the play button ran.
   */
  const [releases, setReleases] = useState([]);
  const [debrid, setDebrid] = useState(false);
  // What the last search said about the language asked for: how many of the
  // releases actually carry it. Read by the audio menu.
  const [audioFound, setAudioFound] = useState(null);
  // Every language this title exists in, with how many releases carry each -
  // counted by the server before its dub gate, so French shows up here even
  // on an original-audio search that dropped the French releases
  const [spokenIn, setSpokenIn] = useState([]);
  // Which release is on screen, and whether it got there by ranking or by being
  // asked for. Both are needed: the menu has to be able to say "Auto — 1080p"
  // and "1080p" as two different states of the same row.
  const [activeHash, setActiveHash] = useState(label.infoHash || null);
  const [auto, setAuto] = useState(true);
  const [switching, setSwitching] = useState(null);

  useEffect(() => {
    if (!label.title) return undefined;

    let cancelled = false;
    searchReleases({
      name: label.title,
      originalName: label.originalName,
      originalLanguage: label.originalLanguage,
      type: label.type,
      imdbId: label.imdbId,
      year: label.year,
      season: label.season ?? undefined,
      episode: label.episode ?? undefined,
      audioLanguage: streamLang
    })
      .then(({ results, meta }) => {
        if (cancelled) return;
        setReleases(results);
        setDebrid(Boolean(meta?.debrid));
        setAudioFound(meta?.audio || null);
        setSpokenIn(meta?.languages || []);
      })
      // No list is not an error worth interrupting playback for: the film is
      // playing, and the menu simply has nothing to offer.
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [label.title, label.type, label.imdbId, label.year, label.season, label.episode, streamLang]);

  /**
   * Ask the server to warm the next episode while this one plays.
   *
   * Starting an episode from nothing is mostly spent finding peers, not moving
   * bytes, and that wait is the whole difference between "put the next one on"
   * and "wait, it's loading again". Doing it ahead of time is what makes the
   * rest of a season feel instant.
   *
   * Deliberately fire-and-forget: the server answers before it starts, decides
   * for itself whether there is anything worth fetching, and holds the download
   * back until nobody is watching. Nothing here needs the answer, and a failure
   * means only that the next episode starts the ordinary way.
   */
  useEffect(() => {
    if (label.type !== 'series' || !label.title) return undefined;
    if (label.season == null || label.episode == null) return undefined;

    // Held off rather than sent on mount. This episode is still buffering its
    // own opening, which is the one moment the connection genuinely cannot
    // spare anything; and someone who switches away in the first minute never
    // wanted the next episode fetched at all.
    const timer = setTimeout(() => {
      axios
        .post('/api/torrents/prefetch', {
          imdbId: label.imdbId || undefined,
          title: label.title,
          originalTitle: label.originalName || undefined,
          originalLanguage: label.originalLanguage || undefined,
          audioLanguage: streamLang,
          season: label.season,
          episode: label.episode + 1
        })
        .catch(() => {});
    }, PREFETCH_AFTER_MS);

    return () => clearTimeout(timer);
  }, [label.type, label.title, label.imdbId, label.season, label.episode, streamLang]);

  // The probe read the real length out of the container, which beats anything
  // the catalog claims; before it lands, the type's assumed runtime stands in.
  const health = useMemo(
    () => ({
      type: label.type,
      runtimeMinutes: probe?.duration ? probe.duration / 60 : undefined
    }),
    [label.type, probe?.duration]
  );

  /**
   * The releases the quality menu chooses between. On a language search that
   * found the language, only those: a quality menu whose 2160p row quietly
   * switches back to the original audio is not a quality menu. When nothing
   * carries it, every release is an original-audio fallback and all are fair.
   */
  const pool = useMemo(() => {
    const matching = releases.filter((entry) => entry.audioMatch);
    return matching.length ? matching : releases;
  }, [releases]);

  const tiers = useMemo(() => qualityTiers(pool, health), [pool, health]);

  // What Auto would land on, which is not simply the best picture: the tier list
  // is ordered by resolution so it reads sensibly, while Auto follows the same
  // playability-first ranking the play button did. Showing `tiers[0]` here would
  // label the row 2160P while Auto was in fact serving 1080P.
  const autoTier = useMemo(() => {
    const best = autoOrder(pool, health)[0];
    return tiers.find((tier) => tier.release.infoHash === best?.infoHash) || tiers[0];
  }, [pool, health, tiers]);
  const activeTier = tiers.find((tier) => tier.release.infoHash === activeHash) || null;

  /**
   * Whether what is on screen was filmed in a cinema, and whether anything else
   * exists.
   *
   * Ranking cams last is most of the answer, but not all of it: a film still in
   * cinemas has nothing *but* cams, so the ranking has nothing to promote over
   * one and it plays anyway. Being handed a camcorder recording of a cinema
   * screen with a betting site's watermark across it, and left to work out for
   * yourself whether that is the film or a fault in the app, is the part worth
   * fixing. So it is said plainly, once, and dismissibly - the alternative is
   * refusing to play the only copy that exists, which is not better.
   */
  const activeRelease = releases.find((entry) => entry.infoHash === activeHash) || null;
  const playingCam = Boolean(activeRelease?.cam);
  const onlyCams = releases.length > 0 && releases.every((entry) => entry.cam);

  // The route state named the release the title page picked, which a quality
  // change makes untrue. Both of these describe what is on screen *now*, so
  // they are read from the switch rather than from where we arrived.
  const subtitle =
    [
      label.season != null && label.episode != null ? `S${label.season} E${label.episode}` : null,
      activeTier?.label || label.resolution || null
    ]
      .filter(Boolean)
      .join(' · ') || label.subtitle;

  const resumeTo = origin.src
    ? `/watch?src=${encodeURIComponent(origin.src)}`
    : `/player/${origin.torrentId}/${origin.fileId}`;

  /**
   * Move to a different release, landing where the film already is.
   *
   * The element is re-pointed rather than the route being replaced, and the
   * position is banked first: `pendingSeek` is applied once the new stream has
   * been probed, because whether the seek is a currentTime or a fresh transcode
   * request depends on what the probe says about the new file.
   */
  const chooseTier = useCallback(
    async (tier, isAuto = false) => {
      // Already the release on screen — this is only a change of how it got there
      if (tier.release.infoHash === activeHash) {
        setAuto(isAuto);
        setMenu(null);
        return;
      }

      setSwitching(isAuto ? 'auto' : tier.key);
      setSwapError('');

      try {
        const target = await startRelease(tier.release, {
          debrid,
          season: label.season ?? undefined,
          episode: label.episode ?? undefined
        });

        // A pack this server can't find the episode in would need the file
        // picker, and dropping out of playback to a file list is not a quality
        // change. Say so and stay where we are.
        if (target.needsPick) {
          setSwapError('That release is a pack this server could not read an episode out of.');
          return;
        }

        pendingSeek.current = current;
        swapping.current = true;
        setActiveHash(tier.release.infoHash);
        setAuto(isAuto);
        setOrigin(target.src ? { src: target.src } : { torrentId: target.torrentId, fileId: target.fileId });
        setMenu(null);
      } catch (err) {
        setSwapError(err.response?.data?.error || 'Could not switch to that quality.');
      } finally {
        setSwitching(null);
      }
    },
    [activeHash, debrid, label.season, label.episode, current]
  );

  /**
   * Hold what is playing up against what was asked for.
   *
   * The name and the file list have already been checked by the time anything
   * plays, but both are only what the uploader wrote. The running time isn't. A
   * half-hour file under an hour-long title is replaced, from the top of the
   * episode, without anybody having to notice it and go looking for the menu.
   * The server remembers, so nobody is handed that file for this episode again.
   *
   * At most two swaps. If the next file runs about as long as the first, it isn't
   * the files that are wrong, it's the catalog's idea of this title's length: the
   * first is cleared and whatever is on screen stays.
   */
  const judged = useRef({ wrong: [], seen: new Set(), played: new Set() });
  useEffect(() => {
    const total = probe?.duration;
    const hash = activeHash;
    const expected = label.runtimeMinutes;
    const state = judged.current;
    // Waits for the release list too: judging before it arrives would use up the
    // one look this release gets, with nothing to report it against.
    if (!total || !hash || !activeRelease || state.seen.has(hash)) return;
    state.seen.add(hash);
    if (!runtimeRulesOut(total, expected)) return;

    const scope = { season: label.season ?? undefined, episode: label.episode ?? undefined };
    const first = state.wrong[0];
    if (first && total / first.duration > 0.85 && total / first.duration < 1.15) {
      reportVerdict(first.release, { ...scope, verdict: 'clear' });
      return;
    }
    if (state.wrong.length >= 2) return;
    state.wrong.push({ release: activeRelease, duration: total });

    (async () => {
      await reportVerdict(activeRelease, { ...scope, verdict: 'wrong', reason: `runs ${Math.round(total / 60)} min` });
      setSwitching('auto');
      try {
        // Asked again rather than filtered here: the server now leaves it out
        const { results, meta } = await searchReleases({
          name: label.title,
          originalName: label.originalName,
          originalLanguage: label.originalLanguage,
          type: label.type,
          imdbId: label.imdbId,
          year: label.year,
          ...scope,
          audioLanguage: streamLang
        });
        const others = results.filter((entry) => !state.wrong.some((bad) => bad.release.infoHash === entry.infoHash));
        if (!others.length) return;

        const { release, target } = await startBest(autoOrder(others, health), { debrid: meta?.debrid, ...scope });
        if (target.needsPick) return;

        pendingSeek.current = 0;
        swapping.current = true;
        setReleases(results);
        setActiveHash(release.infoHash);
        setOrigin(target.src ? { src: target.src } : { torrentId: target.torrentId, fileId: target.fileId });
      } catch (err) {
        // Nothing else opened. What's on screen may yet be right, and the source
        // shouldn't stay condemned on the word of one catalog number.
        reportVerdict(activeRelease, { ...scope, verdict: 'clear' });
      } finally {
        setSwitching(null);
      }
    })();
  }, [probe, activeHash, activeRelease, health, streamLang, label.runtimeMinutes, label.season, label.episode,
    label.title, label.originalName, label.originalLanguage, label.type, label.imdbId, label.year]);

  // Two minutes in, with the right running time, it has earned being tried first
  // next time. Only once per release, whatever the seeking does.
  useEffect(() => {
    const state = judged.current;
    if (current < 120 || !activeRelease || state.played.has(activeRelease.infoHash)) return;
    if (state.wrong.some((bad) => bad.release.infoHash === activeRelease.infoHash)) return;
    state.played.add(activeRelease.infoHash);
    reportVerdict(activeRelease, {
      season: label.season ?? undefined,
      episode: label.episode ?? undefined,
      verdict: 'good'
    });
  }, [current, activeRelease, label.season, label.episode]);

  // The probe settling is the signal that the new stream is understood well
  // enough to know which kind of seek it needs.
  useEffect(() => {
    if (!probe || pendingSeek.current == null) return;

    const at = pendingSeek.current;
    pendingSeek.current = null;

    if (transcoding) {
      // Transcoding, position *is* the request: `base` re-points src at a stream
      // that begins there.
      swapping.current = true;
      setBase(at);
      setCurrent(at);
      return;
    }

    const video = videoRef.current;
    if (!video) return;
    const land = () => {
      video.currentTime = at;
      setCurrent(at);
    };
    if (video.readyState >= 1) land();
    else video.addEventListener('loadedmetadata', land, { once: true });
  }, [probe, transcoding]);

  /* ----- audio language ------------------------------------------------- */

  /**
   * Put this film in another language, from where it is.
   *
   * Two ways to get there, cheapest first. If the file already carries a track
   * in that language - any MULTI release does - it is only a track change, the
   * same thing the track list below does by hand. Otherwise it is a different
   * release: search again in that language, keep only the releases that carry
   * it, and open the best of them at the current position, exactly as a quality
   * change does.
   *
   * Nothing found is said, not papered over. Quietly carrying on in the
   * original after someone asked for French reads as the switch having
   * worked and the film being wrong.
   */
  const switchLanguage = useCallback(
    async (lang) => {
      setLangNote('');
      const want = langCode(lang);

      // A track in this file first
      if (want) {
        const track = audioTracks.find(
          (entry) => trackIsLanguage(entry, want) && !/commentary/i.test(entry.title || '')
        );
        if (track) {
          if (track.ordinal !== activeAudio) {
            setAudioOrdinal(track.ordinal);
            setBase(current);
            swapping.current = true;
          }
          setStreamLang(lang);
          setMenu(null);
          return;
        }
      }

      // Original, with the playing release already original audio - anything
      // an original search returned, or a fallback a language search marked as
      // not matching: there is no other file to open, only the server's own
      // track pick to go back to.
      if (lang === 'original' && activeRelease && !activeRelease.audioMatch) {
        setAudioOrdinal(null);
        setBase(current);
        swapping.current = true;
        setStreamLang(lang);
        setMenu(null);
        return;
      }

      setLangSwitching(lang);
      try {
        const { results, meta } = await searchReleases({
          name: label.title,
          originalName: label.originalName,
          originalLanguage: label.originalLanguage,
          type: label.type,
          imdbId: label.imdbId,
          year: label.year,
          season: label.season ?? undefined,
          episode: label.episode ?? undefined,
          audioLanguage: lang
        });

        // A language search the server resolved to the original (the language
        // asked for *is* the original) has no matches to filter on - every
        // release it returned is the answer.
        const asked = meta?.audio?.wanted;
        const candidates = asked ? results.filter((entry) => entry.audioMatch) : results;

        if (!candidates.length) {
          setLangNote(
            lang === 'original'
              ? 'No original-language release found for this title.'
              : `No ${audioLanguageLabel(lang)} audio found for this ${
                  label.type === 'series' ? 'episode' : 'title'
                } on the indexers we can reach.`
          );
          return;
        }

        const { release, target } = await startBest(autoOrder(candidates, health), {
          debrid: meta?.debrid ?? debrid,
          season: label.season ?? undefined,
          episode: label.episode ?? undefined
        });

        if (target.needsPick) {
          setLangNote('The only release in that language is a pack this server could not read an episode out of.');
          return;
        }

        pendingSeek.current = current;
        swapping.current = true;
        setReleases(results);
        setAudioFound(meta?.audio || null);
        setActiveHash(release.infoHash);
        setAuto(true);
        setStreamLang(lang);
        setOrigin(target.src ? { src: target.src } : { torrentId: target.torrentId, fileId: target.fileId });
        setMenu(null);
      } catch (err) {
        setLangNote(err.response?.data?.error || 'Could not switch language.');
      } finally {
        setLangSwitching(null);
      }
    },
    [audioTracks, activeAudio, activeRelease, current, debrid, health, label.title, label.originalName,
      label.originalLanguage, label.type, label.imdbId, label.year, label.season, label.episode]
  );

  /**
   * The languages the menu leads with: original, whatever the file carries,
   * whatever the releases found so far carry, and the profile's default. The
   * rest of the list sits behind "More languages" - eighteen rows in a corner
   * menu buries the two anyone is likely to want.
   */
  const langOptions = useMemo(() => {
    const seen = new Set(['original']);
    for (const track of audioTracks) {
      const code = trackLanguageCode(track);
      if (code) seen.add(code);
    }
    for (const entry of releases) for (const code of entry.languages || []) seen.add(code);
    for (const entry of spokenIn) seen.add(entry.code);
    if (preferredAudio()) seen.add(preferredAudio());
    seen.add(streamLang);
    // Original already stands for the work's own language
    if (label.originalLanguage) seen.delete(label.originalLanguage);
    seen.add('original');

    const known = AUDIO_LANGUAGES.filter((entry) => seen.has(entry.code));
    const rest = AUDIO_LANGUAGES.filter(
      (entry) => !seen.has(entry.code) && entry.code !== label.originalLanguage
    );
    return { known, rest };
  }, [audioTracks, releases, spokenIn, streamLang, label.originalLanguage]);

  // How many releases carry each language, for the menu's count column
  const releasesIn = useMemo(() => {
    const counts = new Map();
    for (const entry of spokenIn) counts.set(entry.original ? 'original' : entry.code, entry.count);
    return counts;
  }, [spokenIn]);

  // Which language row is lit: the one asked for, unless the track playing
  // says otherwise - a hand-picked track in a third language lights nothing.
  const playingLang = useMemo(() => {
    if (!chosenTrack) return streamLang;
    const code = trackLanguageCode(chosenTrack);
    if (!code) return streamLang;
    if (code === label.originalLanguage) return 'original';
    return code;
  }, [chosenTrack, streamLang, label.originalLanguage]);

  /* ----- up next ---------------------------------------------------------- */

  /**
   * The episode after this one, or null while unknown / when there isn't one.
   *
   * Answered from the same catalog endpoints the episode list reads, because
   * the release being played knows nothing about it: a season pack doesn't say
   * how many episodes the season has, and the next episode may live in a
   * different torrent entirely. Failing to find out is not an error — the
   * offer simply never appears, and the player ends the way it always did.
   */
  const [nextEp, setNextEp] = useState(null);
  const [ended, setEnded] = useState(false);
  // Whether the end card is allowed to start the countdown. Cancelling is for
  // this episode's credits, not a setting: the next episode asks again.
  const [autoNext, setAutoNext] = useState(true);
  const [countdown, setCountdown] = useState(null);
  const [nextBusy, setNextBusy] = useState(false);
  const [nextError, setNextError] = useState('');

  // `id` and `catalogId` are the same catalog id written twice by `labelFor`,
  // but a row replayed from continue-watching can carry only the second: the
  // rail hands back what it stored, and rows written before `id` existed still
  // have `catalogId` from when it was added for recommendation seeds. Reading
  // one and not the other is why a resumed episode offered nothing.
  const seriesId = label.id || label.catalogId || null;

  useEffect(() => {
    if (label.type !== 'series' || !seriesId || label.season == null || label.episode == null) {
      return undefined;
    }

    let cancelled = false;

    const episodesOf = async (seasonNumber) => {
      const { data } = await axios.get(
        `/api/catalog/title/series/${seriesId}/season/${seasonNumber}`
      );
      return (data.data?.episodes || []).slice().sort((a, b) => a.number - b.number);
    };

    (async () => {
      try {
        // Same season first; the numbering is the catalog's, not an index, so
        // "next" is the smallest number above this one rather than +1 — a
        // season with a gap in it should not end the binge early.
        const here = await episodesOf(label.season);
        const inSeason = here.find((entry) => entry.number > label.episode);
        if (inSeason) {
          if (!cancelled) {
            setNextEp({
              season: label.season,
              episode: inSeason.number,
              name: inSeason.title,
              still: inSeason.still
            });
          }
          return;
        }

        // Season finished: roll into the first episode of the next season up.
        // Specials are season 0 and sort below every real season, so they are
        // never where a finale lands.
        const { data } = await axios.get(`/api/catalog/title/series/${seriesId}`);
        const following = (data.data?.seasons || [])
          .filter((entry) => entry.number > label.season)
          .sort((a, b) => a.number - b.number)[0];
        if (!following) return;

        const there = await episodesOf(following.number);
        const first = there[0];
        if (first && !cancelled) {
          setNextEp({
            season: following.number,
            episode: first.number,
            name: first.title,
            still: first.still
          });
        }
      } catch {
        // No offer this pass; the series page still has the episode list
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [label.type, seriesId, label.season, label.episode]);

  /**
   * Find and open the next episode: the same search → rank → try-in-order the
   * play button runs, then a navigation the route key turns into a fresh
   * player. `replace` keeps the binge out of the history — Back from episode
   * five should mean the series page, not four dead stream URLs.
   */
  const playEpisode = useCallback(async (target) => {
    if (!target || nextBusy) return;

    setNextBusy(true);
    setNextError('');
    setCountdown(null);

    try {
      const { results, meta } = await searchReleases({
        name: label.title,
        type: 'series',
        imdbId: label.imdbId,
        year: label.year,
        season: target.season,
        episode: target.episode,
        // The language this episode is in, not the profile default - a binge
        // switched to French stays French
        audioLanguage: streamLang
      });

      if (!results.length) {
        setNextError('Nothing available for that episode on the indexers we can reach.');
        return;
      }

      const { release, target } = await startBest(
        autoOrder(results, { type: 'series' }),
        // This search's own meta, not the quality menu's copy: whether debrid
        // is up is answered per search, and the menu's answer is an episode old
        { debrid: meta?.debrid, season: target.season, episode: target.episode }
      );

      navigate(routeFor(target), {
        state: labelFor(
          {
            id: seriesId,
            name: label.title,
            // Carried on, or the next episode searches without the original
            // language and the dub filter has nothing to judge against
            originalName: label.originalName,
            originalLanguage: label.originalLanguage,
            backdrop: label.backdrop,
            imdbId: label.imdbId,
            type: label.type,
            year: label.year
          },
          {
            season: target.season,
            episode: target.episode,
            episodeName: target.name,
            release,
            audioLanguage: streamLang
          }
        ),
        replace: true
      });
    } catch (err) {
      setNextError(err.response?.data?.error || 'Could not start that episode.');
    } finally {
      setNextBusy(false);
    }
  }, [nextBusy, label.title, label.imdbId, label.year, seriesId, label.backdrop, label.type,
      label.originalName, label.originalLanguage, streamLang, navigate]);

  const startNext = useCallback(() => playEpisode(nextEp), [playEpisode, nextEp]);

  /**
   * The season's episodes, for the phone's Episodes sheet: fetched when the
   * sheet is first opened (and per season picked in it), never up front - most
   * sittings never open it.
   */
  const [season, setSeason] = useState(null); // { number, episodes, seasons, loading, error }
  const loadSeason = useCallback(
    async (number) => {
      if (!seriesId) return;
      setSeason((was) => ({ ...(was || {}), number, episodes: [], loading: true, error: '' }));
      try {
        const [list, show] = await Promise.all([
          axios.get(`/api/catalog/title/series/${seriesId}/season/${number}`),
          axios.get(`/api/catalog/title/series/${seriesId}`)
        ]);
        const episodes = (list.data.data?.episodes || []).slice().sort((a, b) => a.number - b.number);
        const seasons = (show.data.data?.seasons || [])
          .map((entry) => entry.number)
          .filter((n) => n > 0)
          .sort((a, b) => a - b);
        setSeason({ number, episodes, seasons, loading: false, error: '' });
      } catch {
        setSeason((was) => ({ ...(was || {}), number, loading: false, error: 'Could not load the episodes.' }));
      }
    },
    [seriesId]
  );
  useEffect(() => {
    if (menu === 'episodes' && label.season != null && season?.number == null) loadSeason(label.season);
  }, [menu, label.season, season, loadSeason]);

  // The countdown exists only while the card is up and untouched. Anything
  // that changes that — cancelling, a search already running, a failure that
  // needs reading — tears it down rather than ticking on underneath.
  useEffect(() => {
    if (!ended || !nextEp || !autoNext || nextBusy || nextError) return undefined;

    setCountdown(COUNTDOWN_SECONDS);
    const timer = window.setInterval(
      () => setCountdown((seconds) => (seconds == null ? seconds : seconds - 1)),
      1000
    );

    return () => {
      window.clearInterval(timer);
      setCountdown(null);
    };
  }, [ended, nextEp, autoNext, nextBusy, nextError]);

  useEffect(() => {
    if (countdown === 0) startNext();
  }, [countdown, startNext]);

  const cancelAutoNext = useCallback(() => setAutoNext(false), []);

  /**
   * Everything needed to find this title again from nothing.
   *
   * A resume link cannot be a URL. A torrent id only means something while this
   * server still has that torrent open, and a debrid link is signed and expires
   * within hours — both are dead by the time anyone comes back tomorrow, which
   * is the whole point of the rail. So what gets remembered is the search that
   * found it, and coming back runs that search again.
   */
  const findAgain = useMemo(
    () => ({
      // The catalog id rides along so a resumed episode can still offer the
      // one after it — the rail hands this straight back as route state.
      // Falling back to `catalogId` heals rows stored before `id` existed,
      // which would otherwise replay their null through every future resume.
      id: label.id ?? label.catalogId ?? null,
      title: label.title || null,
      // Remembered alongside the title so resuming from the rail asks the same
      // widened, dub-filtered search the original play did. Older history rows
      // lack these and simply search a little narrower.
      originalName: label.originalName || null,
      originalLanguage: label.originalLanguage || null,
      // So coming back tomorrow resumes in the language it was left in
      audioLanguage: streamLang,
      type: label.type || null,
      imdbId: label.imdbId || null,
      catalogId: label.catalogId || null,
      year: label.year ?? null,
      runtimeMinutes: label.runtimeMinutes ?? null,
      season: label.season ?? null,
      episode: label.episode ?? null,
      backdrop: label.backdrop || null
    }),
    [label.id, label.title, label.originalName, label.originalLanguage, streamLang, label.type, label.imdbId, label.catalogId, label.year, label.season, label.episode, label.backdrop]
  );

  // One row per thing being watched, not per release. Switching quality mid-film
  // opens a different torrent, and so does coming back a week later — keying on
  // the release would leave the rail holding three copies of the same film.
  const historyKey = useMemo(() => {
    const who = label.imdbId || label.title;
    if (!who) return null;
    return [who, label.season ?? '', label.episode ?? ''].join(':');
  }, [label.imdbId, label.title, label.season, label.episode]);

  // Held in a ref rather than closed over: `report` is the cleanup of an effect
  // that re-runs whenever its identity changes, so depending on a value that
  // moves every tick would post a history entry on every tick.
  const position = useRef({ current: 0, duration: 0 });
  position.current = { current, duration };

  // Watch history is what fills the continue-watching rail, so it has to be
  // written while playing rather than only on a clean exit the user may skip.
  const report = useCallback(() => {
    // Film position, not element position: transcoding, the element's clock
    // restarts at each seek and would report the wrong place in the film.
    const { current: at, duration: total } = position.current;
    if (!total || !Number.isFinite(total)) return;

    const progress = at / total;
    axios
      .post('/api/library/history', {
        torrentId: label.torrentId || torrentId,
        key: historyKey,
        resumeTo,
        title: label.title || 'Unknown title',
        subtitle,
        backdrop: label.backdrop,
        progress,
        // Seconds, not just the fraction: resuming re-finds a release whose
        // runtime is its own, and a fraction of the wrong runtime lands in the
        // wrong scene. The timestamp is the thing that survives the switch.
        position: at,
        find: findAgain,
        remaining: minutesLeft(total - at)
      })
      .catch(() => {});
  }, [label, torrentId, historyKey, findAgain, resumeTo, subtitle]);

  useEffect(() => () => report(), [report]);

  /* ----- captions ------------------------------------------------------- */

  /**
   * What identifies the work to the subtitle layer, or null if nothing does.
   *
   * The player is reachable by URL alone, so this arrives as route state and
   * may simply be absent - on a reload, or on a torrent opened directly. That
   * is not an error worth showing: it just means no captions on this pass.
   */
  const ccQuery = useMemo(() => {
    const { imdbId, type, season, episode } = label;
    if (!imdbId) return null;

    const query = new URLSearchParams({ imdbId, type: type === 'series' ? 'series' : 'movie' });
    if (season != null) query.set('season', String(season));
    if (episode != null) query.set('episode', String(episode));
    return query.toString();
  }, [label.imdbId, label.type, label.season, label.episode]);

  useEffect(() => {
    if (!ccQuery) return undefined;

    const query = new URLSearchParams(ccQuery);
    // The remembered language rides along so the server's own pick already
    // honours it, rather than the client second-guessing the ranking after.
    const wanted = localStorage.getItem(PREF.language);
    if (wanted) query.set('languages', wanted);

    let cancelled = false;
    setCaptions('loading');
    // A new film is a clean slate for the fallback bookkeeping
    setFailed([]);
    setDead(false);

    let retry = null;
    const ask = (attempt) => {
      axios
        .get(`/api/subtitles/search?${query}`)
        .then(({ data }) => {
          if (cancelled) return;
          const found = data.data.tracks || [];
          // Nothing found *because* a source failed is not "no captions"; it
          // is a round to repeat, with a pause for the source to recover.
          const degraded = (data.meta?.sources || []).some((entry) => !entry.ok);
          if (!found.length && degraded && attempt < CC_SEARCH_RETRIES) {
            retry = window.setTimeout(() => ask(attempt + 1), CC_RETRY_MS);
            return;
          }
          setTracks(found);
          setLanguages(data.data.languages || []);
          setTrackId(data.data.bestId || '');
          setCaptions(found.length ? 'ready' : 'none');
        })
        .catch(() => {
          if (cancelled) return;
          if (attempt < CC_SEARCH_RETRIES) {
            retry = window.setTimeout(() => ask(attempt + 1), CC_RETRY_MS);
            return;
          }
          setCaptions('error');
        });
    };
    ask(0);

    return () => {
      cancelled = true;
      window.clearTimeout(retry);
    };
  }, [ccQuery]);

  const vttUrl = useMemo(() => {
    if (!ccQuery || !trackId) return '';
    const query = new URLSearchParams(ccQuery);
    query.set('id', trackId);
    return `/api/subtitles/vtt?${query}`;
  }, [ccQuery, trackId]);

  /**
   * The cues are drawn by this component, not by the browser.
   *
   * `hidden` still parses the file; it only declines to paint. That is what we
   * want: browser-rendered captions sit *under* the control chrome and ignore
   * the player's type, and there is no reliable way to style them across
   * engines. So we take the parse and do the drawing.
   *
   * The parse is *all* we take. The browser's own activeCues match against the
   * element's clock, and on a transcoded stream that clock restarts at zero
   * meaning `base` film-seconds in - so cues written in film seconds never
   * matched it. Mutating the cue times to compensate was tried and lost
   * information every time the picture reloaded: a stall-resume swaps src, the
   * browser quietly re-fetches the track, the fresh cues arrive unshifted
   * while the bookkeeping still said "shifted", and the captions went silent
   * until the next seek - which read, from the sofa, as subtitles covering
   * two-thirds of the dialogue. So the cues stay exactly as written and the
   * lookup below runs against the player's own absolute clock instead, which
   * is immune to src swaps and re-fetches by construction.
   */
  useEffect(() => {
    const element = ccRef.current;
    setCue('');
    if (!element) return undefined;

    const textTrack = element.track;
    textTrack.mode = 'hidden';
    setDead(false);

    // `default` lets the browser turn the track on by itself once it has
    // loaded, which can land after the line above - and then the browser
    // draws the cues too, a second copy along the bottom of the picture. Seen
    // on phones, where the picture's bottom edge is not under the controls.
    const keepHidden = () => {
      if (textTrack.mode === 'showing') textTrack.mode = 'hidden';
    };
    element.addEventListener('load', keepHidden);
    const list = videoRef.current?.textTracks;
    list?.addEventListener?.('change', keepHidden);

    // A track that 404s, rate-limits or arrives unparseable fails silently:
    // the element errors, the cue list stays empty, and the button goes on
    // looking lit. The fix is the next file in the same language, so that is
    // tried without asking - a file nobody chose is nobody's to defend - and
    // only when the language is exhausted does the menu say so.
    const onError = () => {
      const failing = trackId;
      setFailed((previous) => (previous.includes(failing) ? previous : [...previous, failing]));
      setTracks((list) => {
        const current = list.find((entry) => entry.id === failing);
        const next = current
          ? list.find(
              (entry) =>
                entry.language === current.language && entry.id !== failing && !failedRef.current.includes(entry.id)
            )
          : null;
        if (next) {
          setTrackId(next.id);
          setOffset(0);
        } else {
          setDead(true);
        }
        return list;
      });
    };
    element.addEventListener('error', onError);

    return () => {
      element.removeEventListener('error', onError);
      element.removeEventListener('load', keepHidden);
      list?.removeEventListener?.('change', keepHidden);
      textTrack.mode = 'disabled';
    };
  }, [vttUrl, ccOn, trackId]);

  // The active cue, looked up by film time. `current` is already base +
  // currentTime, so this is right on the direct path and the transcode path
  // alike; the viewer's nudge shifts the comparison, not the cues. A linear
  // scan over a film's worth of cues a few times a second costs nothing.
  useEffect(() => {
    if (!ccOn) return;
    const cues = ccRef.current?.track?.cues;
    if (!cues || !cues.length) return;

    // Subtitle seconds for this film second: undo the auto scale and both
    // shifts. `ccAuto.scale` is 1 unless the file runs at a different frame rate.
    const at = (current - offset - ccAuto.offset) / ccAuto.scale;
    const lines = [];
    for (let i = 0; i < cues.length; i += 1) {
      if (cues[i].startTime <= at && at < cues[i].endTime) {
        const line = plain(cues[i].text);
        if (line) lines.push(line);
      }
    }
    const next = lines.join('\n');
    setCue((previous) => (previous === next ? previous : next));
  }, [current, offset, ccAuto, ccOn, vttUrl]);

  /**
   * Automatic sync, whenever a track is loaded against a stream.
   *
   * The server listens to the opening minutes of the file and correlates the
   * speech it hears with the cues; the answer arrives while the film is
   * already playing and the captions simply move. A weak match is not applied
   * - a guessed offset is worse than none - and the menu says so. A quality
   * switch is a different file, so it asks again.
   */
  useEffect(() => {
    if (!ccQuery || !trackId || !streamUrl || !ccOn) return undefined;

    let cancelled = false;
    setCcAuto({ status: 'running', offset: 0, scale: 1, confidence: 0 });

    const query = new URLSearchParams(ccQuery);
    query.set('id', trackId);
    query.set('src', streamUrl);

    axios
      .get(`/api/subtitles/sync?${query}`, { timeout: 300000 })
      .then(({ data }) => {
        if (cancelled) return;
        const found = data.data || {};
        if (found.available === false) {
          setCcAuto({ status: 'unavailable', offset: 0, scale: 1, confidence: 0 });
        } else if (found.confident) {
          setCcAuto({ status: 'done', offset: found.offset, scale: found.scale || 1, confidence: found.confidence });
        } else {
          setCcAuto({ status: 'weak', offset: 0, scale: 1, confidence: found.confidence || 0 });
        }
      })
      .catch(() => {
        if (!cancelled) setCcAuto({ status: 'failed', offset: 0, scale: 1, confidence: 0 });
      });

    return () => {
      cancelled = true;
    };
  }, [ccQuery, trackId, streamUrl, ccOn, ccAutoRun]);

  const nudge = useCallback((delta) => {
    setOffset((previous) => {
      const next = Math.min(Math.max(previous + delta, -SYNC_LIMIT), SYNC_LIMIT);
      return Math.round(next * 10) / 10;
    });
  }, []);

  const toggleCaptions = useCallback(() => {
    setCcOn((previous) => {
      localStorage.setItem(PREF.enabled, previous ? 'off' : 'on');
      return !previous;
    });
  }, []);

  /** Switch language, taking the best-ranked track in it - `tracks` is ranked. */
  const chooseLanguage = useCallback(
    (code) => {
      const best = tracks.find((entry) => entry.language === code);
      if (!best) return;
      localStorage.setItem(PREF.language, code);
      localStorage.setItem(PREF.enabled, 'on');
      setTrackId(best.id);
      setCcOn(true);
      setOffset(0);
    },
    [tracks]
  );

  const chooseTrack = useCallback((entry) => {
    localStorage.setItem(PREF.language, entry.language);
    localStorage.setItem(PREF.enabled, 'on');
    setTrackId(entry.id);
    setCcOn(true);
    // A different file is a different sync problem; carrying the old
    // correction over would start it off wrong by exactly that much.
    setOffset(0);
  }, []);

  const selected = tracks.find((entry) => entry.id === trackId) || null;
  const inLanguage = selected ? tracks.filter((entry) => entry.language === selected.language) : [];

  /* ----- casting --------------------------------------------------------- */

  /**
   * Whether there is a TV to send this to, and whether we're on it.
   *
   * Two APIs for one idea: Chrome (and the Chromiums) speak the standard
   * Remote Playback API, which reaches Chromecasts; Safari never implemented
   * it and exposes AirPlay through its own webkit events instead. Both are
   * watched, whichever answers first wins, and neither existing just means the
   * button never appears.
   */
  const [castTargets, setCastTargets] = useState(false);
  const [casting, setCasting] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const cleanups = [];

    const remote = video.remote;
    if (remote?.watchAvailability) {
      let watchId = null;
      remote
        .watchAvailability((available) => setCastTargets(available))
        .then((id) => {
          watchId = id;
        })
        // Refusal to watch is not "no devices" — some browsers decline the
        // continuous scan on battery. The button stays hidden, which is the
        // honest answer for "we can't tell".
        .catch(() => {});

      const connected = () => setCasting(true);
      const disconnected = () => setCasting(false);
      remote.addEventListener('connect', connected);
      remote.addEventListener('disconnect', disconnected);
      cleanups.push(() => {
        remote.removeEventListener('connect', connected);
        remote.removeEventListener('disconnect', disconnected);
        if (watchId != null) remote.cancelWatchAvailability(watchId).catch(() => {});
      });
    }

    if (window.WebKitPlaybackTargetAvailabilityEvent) {
      const onAvailability = (event) => setCastTargets(event.availability === 'available');
      const onTarget = () => setCasting(Boolean(video.webkitCurrentPlaybackTargetIsWireless));
      video.addEventListener('webkitplaybacktargetavailabilitychanged', onAvailability);
      video.addEventListener('webkitcurrentplaybacktargetiswirelesschanged', onTarget);
      cleanups.push(() => {
        video.removeEventListener('webkitplaybacktargetavailabilitychanged', onAvailability);
        video.removeEventListener('webkitcurrentplaybacktargetiswirelesschanged', onTarget);
      });
    }

    return () => cleanups.forEach((undo) => undo());
    // Re-attached after the boot screen unmounts the element and again if a
    // failed stream replaces it — the listeners live on the element itself.
  }, [loading, tokenSettled, error]);

  const promptCast = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    // Safari's picker is the AirPlay menu; everything else speaks the standard
    if (video.webkitShowPlaybackTargetPicker && window.WebKitPlaybackTargetAvailabilityEvent) {
      video.webkitShowPlaybackTargetPicker();
      return;
    }
    video.remote?.prompt().catch(() => {
      // Declining the picker is not an error the player needs words for
    });
  }, []);

  /* ----- transport ------------------------------------------------------ */

  const wake = useCallback(() => {
    setIdle(false);
    window.clearTimeout(idleTimer.current);
    idleTimer.current = window.setTimeout(() => setIdle(true), touchUI ? IDLE_TOUCH_MS : IDLE_MS);
  }, [touchUI]);

  // A tap on a phone hides the controls as readily as it shows them
  const sleep = useCallback(() => {
    window.clearTimeout(idleTimer.current);
    setIdle(true);
  }, []);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) video.play().catch(() => {});
    else video.pause();
  }, []);

  /**
   * Move to an absolute position in the film.
   *
   * Playing direct this is just currentTime. Transcoding it cannot be: the
   * response is produced as it is sent, so there are no byte ranges to ask for
   * and the element only ever holds the stretch from `base` onwards. Seeking is
   * therefore a new request starting where the viewer asked, and the element's
   * own clock restarts at zero meaning `base` seconds in.
   *
   * That restart is why the position everything else reads is `base +
   * currentTime` rather than currentTime.
   */
  const seekAbsolute = useCallback(
    (seconds) => {
      const video = videoRef.current;
      if (!video || !duration) return;
      const target = Math.min(Math.max(seconds, 0), duration);

      if (transcoding) {
        // Nothing is set on the element: changing `base` re-points src, and the
        // browser loads a stream that begins at exactly this point.
        //
        // Swapping src aborts the response in flight and kills the ffmpeg
        // feeding it, which the element can surface as an error. That is the
        // seek working, not the stream failing, so it is suppressed until the
        // replacement is playing.
        swapping.current = true;
        setBase(target);
        setCurrent(target);
        return;
      }

      video.currentTime = target;
      setCurrent(target);
    },
    [duration, transcoding]
  );

  /**
   * Pick a transcoded stream back up where it stopped.
   *
   * A transcoded response is generated live and cannot be range-resumed, so the
   * pipe behind it dying - debrid link, remux, proxy - leaves the browser with
   * nothing it can do on its own. Restarting from where the film actually was is
   * strictly better than the error screen, and it must be from base +
   * currentTime, not the old base: resuming at the same spot forever was the
   * "keeps loading and starting" loop, replaying the same seconds on every
   * death. Only real progress renews the budget, so a stream dying instantly
   * every time still runs out rather than looping.
   *
   * Returns false when the budget is spent and the caller should give up.
   */
  const resumeAfterStall = useCallback(() => {
    const video = videoRef.current;
    const played = video?.currentTime || 0;
    if (played > 1) stallRetries.current = 0;
    if (stallRetries.current >= STALL_RETRIES) return false;

    stallRetries.current += 1;
    swapping.current = true;
    setWaiting(true);

    const target = base + played;
    if (target === base) {
      // A stream that died before producing anything leaves `src` unchanged, so
      // re-rendering asks the browser for nothing and the film sits behind a
      // spinner that never resolves. Asking the element itself is the only way
      // to make the same request twice.
      video?.load();
      return true;
    }

    setBase(target);
    setCurrent(target);
    return true;
  }, [base]);

  const skip = useCallback(
    (delta) => {
      if (!duration) return;
      seekAbsolute(current + delta);

      setFlash(delta < 0 ? 'back' : 'fwd');
      window.clearTimeout(flashTimer.current);
      flashTimer.current = window.setTimeout(() => setFlash(null), 500);
    },
    [current, duration, seekAbsolute]
  );

  const toggleFullscreen = useCallback(() => {
    const shell = shellRef.current;
    const video = videoRef.current;
    if (!shell) return;

    if (document.fullscreenElement || document.webkitFullscreenElement) {
      (document.exitFullscreen || document.webkitExitFullscreen).call(document);
      return;
    }
    // On a phone a widescreen film wants the phone on its side, so the screen
    // is locked to landscape where the browser allows it (Android, while
    // fullscreen); unlocked again on the way out, below
    const lock = () => {
      if (!touchUI || !video || video.videoWidth < video.videoHeight) return;
      window.screen?.orientation?.lock?.('landscape')?.catch(() => {});
    };
    if (shell.requestFullscreen) shell.requestFullscreen().then(lock, () => {});
    else if (shell.webkitRequestFullscreen) {
      shell.webkitRequestFullscreen();
      lock();
    }
    // iPhone Safari never gives an arbitrary element the screen, only the video
    else if (video?.webkitEnterFullscreen) video.webkitEnterFullscreen();
  }, [touchUI]);

  const changeVolume = useCallback((value) => {
    const video = videoRef.current;
    if (!video) return;
    const next = Math.min(Math.max(value, 0), 1);
    video.volume = next;
    video.muted = next === 0;
  }, []);

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    // Unmuting at zero volume looks broken, so give it something audible back
    if (video.muted && video.volume === 0) video.volume = 0.5;
    video.muted = !video.muted;
  }, []);

  useEffect(() => {
    const onChange = () => {
      const now = Boolean(document.fullscreenElement || document.webkitFullscreenElement);
      setFullscreen(now);
      if (!now) {
        try {
          window.screen?.orientation?.unlock?.();
        } catch {
          // Nothing was locked
        }
      }
    };
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, []);

  useEffect(() => {
    const onKey = (event) => {
      if (event.target.closest?.('input, textarea, [contenteditable]')) return;

      const handled = {
        ' ': togglePlay,
        k: togglePlay,
        ArrowLeft: () => skip(-SKIP_SECONDS),
        ArrowRight: () => skip(SKIP_SECONDS),
        ArrowUp: () => changeVolume((videoRef.current?.volume ?? 0) + 0.05),
        ArrowDown: () => changeVolume((videoRef.current?.volume ?? 0) - 0.05),
        f: toggleFullscreen,
        m: toggleMute,
        c: toggleCaptions,
        // Bracketed either side of the fast-forward keys, and reachable without
        // opening the menu - drift is noticed mid-scene, not from a menu.
        '[': () => nudge(-SYNC_STEP),
        ']': () => nudge(SYNC_STEP),
        // Only while the end card is up: mid-film Enter means nothing, and
        // giving it a meaning there would make every stray keypress a skip.
        Enter: ended && nextEp && !nextBusy ? startNext : undefined,
        // Escape belongs to fullscreen unless a menu — or the countdown — has
        // a stronger claim on "no, stop that"
        Escape:
          menu ? () => setMenu(null) : countdown != null ? cancelAutoNext : undefined
      }[event.key.length === 1 ? event.key.toLowerCase() : event.key];

      if (!handled) return;
      event.preventDefault();
      handled();
      wake();
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    togglePlay,
    skip,
    changeVolume,
    toggleFullscreen,
    toggleMute,
    toggleCaptions,
    nudge,
    menu,
    wake,
    ended,
    nextEp,
    nextBusy,
    startNext,
    countdown,
    cancelAutoNext
  ]);


  useEffect(() => {
    wake();
    return () => {
      window.clearTimeout(idleTimer.current);
      window.clearTimeout(flashTimer.current);
    };
  }, [wake]);

  /* ----- scrubbing ------------------------------------------------------ */

  const ratioFromEvent = (event) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || !rect.width) return 0;
    return Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1);
  };

  /**
   * Live scrubbing: the picture follows the pointer while the bar is held,
   * not only once it is let go.
   *
   * Playing direct, the element itself is sought - but at most once per
   * animation frame and with one seek in flight at a time. Pointer events
   * arrive far faster than a seek completes, and each seek on a torrent or
   * debrid source is a new range request; following every one would queue
   * dozens behind each other and the picture would trail the pointer by
   * seconds. So the latest position is kept, a frame callback sends it, and
   * the next is only sent when `seeked` says the last one landed. Mid-drag
   * seeks use fastSeek (the nearest keyframe, cheap to show) where the
   * browser has it; release does one exact seek. The film is paused while
   * held so it doesn't play a few frames between seeks, and picks up again
   * on release if it was playing.
   *
   * Transcoding, seeking the element means a new ffmpeg per seek, so the
   * stream is left alone until release and the server is asked for stills
   * instead (/api/transcode/frame), also one at a time, shown over the
   * picture. The last still stays up until the stream from the new position
   * starts, rather than flashing to black in between.
   */
  const scrubTarget = useRef(0);
  const scrubFrame = useRef(0);
  const scrubTimer = useRef(0);
  const seekInFlight = useRef(false);
  const lastSent = useRef(-1);
  const resumeAfterScrub = useRef(false);
  const [stillSrc, setStillSrc] = useState(null);
  const stillLoading = useRef(false);
  const stillWanted = useRef(null);

  const sendSeek = useCallback(() => {
    cancelAnimationFrame(scrubFrame.current);
    window.clearTimeout(scrubTimer.current);
    scrubFrame.current = 0;
    scrubTimer.current = 0;
    const video = videoRef.current;
    if (!video || seekInFlight.current) return;
    const target = scrubTarget.current;
    if (Math.abs(target - lastSent.current) < 0.05) return;
    seekInFlight.current = true;
    lastSent.current = target;
    if (typeof video.fastSeek === 'function') video.fastSeek(target);
    else video.currentTime = target;
  }, []);

  // A still for the transcoded case: one request at a time, the newest wins
  const loadStill = useCallback(
    (seconds) => {
      stillWanted.current = seconds;
      if (stillLoading.current || !streamUrl) return;
      stillLoading.current = true;
      const at = Math.round(seconds);
      const query = new URLSearchParams({ src: streamUrl, t: String(at), w: '640' });
      if (mediaToken) query.set('token', mediaToken);
      const url = `/api/transcode/frame?${query}`;
      const img = new Image();
      const done = () => {
        stillLoading.current = false;
        const next = stillWanted.current;
        if (next !== null && Math.round(next) !== at) loadStill(next);
      };
      img.onload = () => {
        setStillSrc(url);
        done();
      };
      // A still that can't be had is no reason to stop; the drag carries on
      // and release still seeks
      img.onerror = done;
      img.src = url;
    },
    [streamUrl, mediaToken]
  );

  const scrubTo = (seconds) => {
    scrubTarget.current = seconds;
    setCurrent(seconds);
    if (transcoding) {
      loadStill(seconds);
      return;
    }
    // Next frame, or ~30 ms if frames aren't being drawn (a throttled or
    // hidden page): whichever comes first sends it, and it clears the other
    if (!scrubFrame.current && !scrubTimer.current) {
      scrubFrame.current = requestAnimationFrame(sendSeek);
      scrubTimer.current = window.setTimeout(sendSeek, 34);
    }
  };

  const tipAt = (ratio) => ({ ratio, x: ratio * (trackRef.current?.getBoundingClientRect().width || 0) });

  const onTrackDown = (event) => {
    if (!duration) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    const video = videoRef.current;
    resumeAfterScrub.current = Boolean(video && !video.paused);
    // Held, nothing should play on under the drag: direct, it would advance
    // between seeks; transcoding, its sound would carry on over the stills
    if (video) video.pause();
    lastSent.current = -1;
    setScrubbing(true);
    if (event.pointerType !== 'mouse') haptic(8);
    const ratio = ratioFromEvent(event);
    // The tooltip rides the knob while held, touch included
    setHover(tipAt(ratio));
    scrubTo(ratio * duration);
  };

  const onTrackMove = (event) => {
    const ratio = ratioFromEvent(event);
    setHover(scrubbing ? tipAt(ratio) : { ratio, x: event.clientX - (trackRef.current?.getBoundingClientRect().left || 0) });
    if (!scrubbing) return;
    scrubTo(ratio * duration);
  };

  const onTrackUp = (event) => {
    if (!scrubbing) return;
    event.currentTarget.releasePointerCapture(event.pointerId);
    cancelAnimationFrame(scrubFrame.current);
    window.clearTimeout(scrubTimer.current);
    scrubFrame.current = 0;
    scrubTimer.current = 0;
    stillWanted.current = null;
    setScrubbing(false);
    if (event.pointerType !== 'mouse') setHover(null);
    // The exact seek: mid-drag ones may have landed on a nearby keyframe, and
    // transcoding this is the only real seek of the drag
    seekAbsolute(scrubTarget.current);
    if (transcoding) {
      // Played on by the new stream itself; the last still stays until it
      // starts, or a few seconds at most if the position didn't change
      window.setTimeout(() => setStillSrc(null), 8000);
    } else {
      seekInFlight.current = false;
      if (resumeAfterScrub.current) videoRef.current?.play().catch(() => {});
    }
    report();
  };

  // A drag abandoned mid-seek must not leave a frame callback behind
  useEffect(
    () => () => {
      cancelAnimationFrame(scrubFrame.current);
      window.clearTimeout(scrubTimer.current);
    },
    []
  );

  /* ----- touch ---------------------------------------------------------- */

  /**
   * The phone's gestures, on a layer over the picture and under the controls.
   *
   *   tap                 show the controls, or hide them if they are up
   *   double tap a side   back or forward 10 s, with a ripple where it landed;
   *                       further taps on that side keep going, 10 s each
   *   drag down, middle   pull the player away; far or fast enough, it closes
   *   drag up/down, left  dim the picture (a phone page cannot set the screen's
   *                       own brightness, so this is an overlay)
   *   drag up/down, right volume - not on iOS, where a page cannot set it and
   *                       the side buttons are the volume
   *
   * Nothing starts within EDGE_PX of the sides, where Safari's back swipe is.
   * The middle third has no double tap, so a tap there answers at once rather
   * than waiting to see whether a second is coming.
   */
  const previewRef = useRef(null);
  const gesture = useRef(null);
  const tapTimer = useRef(0);
  const lastTap = useRef(null); // { side, at }
  const streak = useRef(null); // { side, until, total }
  const [seekBubble, setSeekBubble] = useState(null); // { side, total, x, y, key }
  const bubbleTimer = useRef(0);
  const [hud, setHud] = useState(null); // { kind: 'volume' | 'brightness', level }
  const hudTimer = useRef(0);
  const [dim, setDim] = useState(1); // 1 = untouched, down to 0.25
  const [pull, setPull] = useState(0);
  const canSetVolume = !isIOS();

  useEffect(
    () => () => {
      window.clearTimeout(tapTimer.current);
      window.clearTimeout(bubbleTimer.current);
      window.clearTimeout(hudTimer.current);
    },
    []
  );

  // In landscape a phone wants the whole screen. Fullscreen can only be asked
  // for inside a tap, so a rotation tries (some browsers allow it) and the
  // next tap on the picture asks again. iPhone has no element fullscreen -
  // its only fullscreen is the system player, which would trade these controls
  // for Apple's - so there the landscape layout is the fullscreen.
  const wantFullscreen = useCallback(() => {
    if (!touchUI || document.fullscreenElement || document.webkitFullscreenElement) return;
    if (!window.matchMedia('(orientation: landscape)').matches) return;
    const shell = shellRef.current;
    if (shell?.requestFullscreen) shell.requestFullscreen().catch(() => {});
    else if (shell?.webkitRequestFullscreen) shell.webkitRequestFullscreen();
  }, [touchUI]);

  useEffect(() => {
    if (!touchUI) return undefined;
    const query = window.matchMedia('(orientation: landscape)');
    const onTurn = () => {
      if (query.matches) wantFullscreen();
    };
    query.addEventListener?.('change', onTurn);
    return () => query.removeEventListener?.('change', onTurn);
  }, [touchUI, wantFullscreen]);

  const showHud = (kind, level) => {
    setHud({ kind, level });
    window.clearTimeout(hudTimer.current);
    hudTimer.current = window.setTimeout(() => setHud(null), 700);
  };

  const sideOf = (x, width) => (x < width / 3 ? 'back' : x > (width * 2) / 3 ? 'fwd' : 'mid');

  const seekBy = (side, x, y) => {
    const delta = side === 'back' ? -SKIP_SECONDS : SKIP_SECONDS;
    const now = Date.now();
    const going = streak.current && streak.current.side === side && streak.current.until > now;
    const total = (going ? streak.current.total : 0) + SKIP_SECONDS;
    streak.current = { side, until: now + SEEK_STREAK_MS, total };
    seekAbsolute(current + delta);
    haptic(10);
    setSeekBubble({ side, total, x, y, key: now });
    window.clearTimeout(bubbleTimer.current);
    bubbleTimer.current = window.setTimeout(() => setSeekBubble(null), SEEK_STREAK_MS);
  };

  const onGestureDown = (event) => {
    if (event.pointerType === 'mouse') return;
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    gesture.current = {
      id: event.pointerId,
      x0: event.clientX,
      y0: event.clientY,
      t0: event.timeStamp,
      x,
      y: event.clientY - rect.top,
      width: rect.width,
      height: rect.height,
      edge: x < EDGE_PX || x > rect.width - EDGE_PX,
      mode: null,
      start: 0
    };
  };

  const onGestureMove = (event) => {
    const g = gesture.current;
    if (!g || g.id !== event.pointerId) return;
    const dx = event.clientX - g.x0;
    const dy = event.clientY - g.y0;

    if (!g.mode) {
      if (Math.abs(dy) < SWIPE_SLOP || Math.abs(dy) < Math.abs(dx) * 1.2) {
        if (Math.abs(dx) > SWIPE_SLOP * 2) g.mode = 'none';
        return;
      }
      if (g.edge) {
        g.mode = 'none';
        return;
      }
      const side = sideOf(g.x, g.width);
      if (side === 'back') {
        g.mode = 'brightness';
        g.start = dim;
      } else if (side === 'fwd') {
        // Without volume (iOS) the right third does nothing: a stray swipe
        // there closing the film would be worse than no gesture at all
        g.mode = canSetVolume ? 'volume' : 'none';
        const video = videoRef.current;
        g.start = video ? (video.muted ? 0 : video.volume) : 1;
      } else {
        g.mode = dy > 0 ? 'close' : 'none';
      }
      event.currentTarget.setPointerCapture?.(event.pointerId);
    }

    // Two thirds of the screen's height is the full range: a thumb's reach
    const span = g.height * 0.66;
    if (g.mode === 'brightness') {
      const level = Math.min(1, Math.max(0.25, g.start - dy / span));
      setDim(level);
      showHud('brightness', (level - 0.25) / 0.75);
    } else if (g.mode === 'volume') {
      const level = Math.min(1, Math.max(0, g.start - dy / span));
      changeVolume(level);
      showHud('volume', level);
    } else if (g.mode === 'close') {
      setPull(Math.max(0, dy));
    }
  };

  const onGestureUp = (event) => {
    const g = gesture.current;
    gesture.current = null;
    if (!g || g.id !== event.pointerId) return;

    if (g.mode === 'close') {
      const dy = event.clientY - g.y0;
      const velocity = dy / Math.max(1, event.timeStamp - g.t0);
      if (dy > CLOSE_PULL_PX || velocity > 0.7) {
        haptic(12);
        navigate(-1);
      } else {
        setPull(0);
      }
      return;
    }
    if (g.mode) return;
    if (event.type === 'pointercancel') return;

    // A tap. Landscape and not yet fullscreen: this tap is the gesture
    // fullscreen needs
    wantFullscreen();

    const side = sideOf(g.x, g.width);
    const now = Date.now();

    // Mid-streak, a tap on the same side is one more seek, at once
    if (side !== 'mid' && streak.current?.side === side && streak.current.until > now) {
      window.clearTimeout(tapTimer.current);
      lastTap.current = null;
      seekBy(side, g.x, g.y);
      return;
    }

    if (side !== 'mid' && lastTap.current?.side === side && now - lastTap.current.at < DOUBLE_TAP_MS) {
      window.clearTimeout(tapTimer.current);
      lastTap.current = null;
      seekBy(side, g.x, g.y);
      return;
    }

    const toggle = () => {
      if (hideChrome) wake();
      else sleep();
    };

    if (side === 'mid') {
      lastTap.current = null;
      toggle();
      return;
    }
    lastTap.current = { side, at: now };
    window.clearTimeout(tapTimer.current);
    tapTimer.current = window.setTimeout(() => {
      lastTap.current = null;
      toggle();
    }, DOUBLE_TAP_MS);
  };

  // While scrubbing on a phone, the bubble over the thumb shows the frame
  // being landed on: the transcoder's still when there is one, otherwise the
  // picture itself, copied each time a mid-drag seek lands
  const drawPreview = () => {
    const canvas = previewRef.current;
    const video = videoRef.current;
    if (!canvas || !video || !video.videoWidth) return;
    try {
      canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height);
    } catch {
      // A frame that cannot be drawn leaves the last one up
    }
  };

  /* ----- media events --------------------------------------------------- */

  // The element's clock starts at zero on every transcode segment, so both the
  // playhead and the buffered range are `base` seconds further into the film
  // than the element believes. Playing direct, `base` is 0 and this is a no-op.
  const syncBuffered = () => {
    const video = videoRef.current;
    if (!video) return;
    const ranges = video.buffered;
    for (let i = 0; i < ranges.length; i += 1) {
      if (ranges.start(i) <= video.currentTime && video.currentTime <= ranges.end(i)) {
        setBuffered(base + ranges.end(i));
        return;
      }
    }
    setBuffered(0);
  };

  const onTimeUpdate = () => {
    const video = videoRef.current;
    if (!video) return;
    if (!scrubbing) setCurrent(base + video.currentTime);
    syncBuffered();

    const now = Date.now();
    if (now - lastReport.current < REPORT_EVERY_MS) return;
    lastReport.current = now;
    report();
  };

  /**
   * Notice when the browser refuses to start on its own.
   *
   * A phone only plays a video with sound while a tap is in progress, and by
   * the time the source is resolved and probed that tap is seconds old. The
   * element then sits paused with nothing loaded, which read as `busy` - so
   * the iPhone showed a spinner over the artwork forever, when one tap on Play
   * was all it needed. Asking play() directly is the only way to hear the no.
   */
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !playSrc || loading || !tokenSettled) return;
    video.play()?.catch((err) => {
      if (err?.name !== 'NotAllowedError') return;
      setAutoplayBlocked(true);
      setWaiting(false);
    });
  }, [playSrc, loading, tokenSettled]);

  const pct = (seconds) => (duration ? `${Math.min(seconds / duration, 1) * 100}%` : '0%');
  // A drag fires a seek every frame, and each one is `waiting` for a moment;
  // a spinner flickering over the frames being scrubbed would hide them
  const busy = !autoplayBlocked && ((waiting && !scrubbing) || (!duration && !error));

  // The standing offer over the credits: there is a next episode and the film
  // is inside its final minute but not yet over. `current > 0` keeps it from
  // flickering up while a fresh stream still reports a zero-length duration.
  const upNextSoon =
    nextEp && !ended && duration > 0 && current > 0 && duration - current <= NEXT_OFFER_SECONDS;
  const endCard = ended && nextEp;

  // Reading the caption menu takes longer than the idle timeout, and a menu
  // vanishing from under the pointer is worse than chrome that overstays.
  const hideChrome = idle && playing && !scrubbing && !menu;

  // The film's artwork, standing in until the picture takes over. Route state
  // carries it; a stream opened by bare URL has none and gets the old black.
  const cover = label.backdrop ? (
    <div className="player-cover">
      <img src={label.backdrop} alt="" />
      <div className="player-cover-wash" />
    </div>
  ) : null;

  // The token settling is part of "opening": it has to be in the src from the
  // first frame, and it is one fast local request either way.
  if (loading || !tokenSettled) {
    return (
      <div className="player">
        {cover}
        <div className="player-boot">
          <span className="player-ring" />
          <span className="player-boot-text">OPENING STREAM</span>
        </div>
      </div>
    );
  }

  if (error) {
    // A dead end says what died and offers the way out, over the film's own
    // artwork rather than a black screen with one red line on it
    return (
      <div className="player">
        {label.backdrop && <img className="player-fail-art" src={label.backdrop} alt="" />}
        <div className="player-boot">
          <div className="player-fail" role="alert">
            <div className="rs-kicker">
              <span className="rs-dot" />
              {label.isTrailer ? 'TRAILER · PLAYBACK STOPPED' : 'PLAYBACK STOPPED'}
            </div>
            <h2 className="player-fail-title">{label.title || 'This stream'}</h2>
            {label.subtitle && <div className="player-sub">{label.subtitle}</div>}
            <p className="player-fail-msg">{error}</p>
            <div className="rs-actions">
              <button type="button" className="rs-trailer rs-trailer--primary" onClick={() => navigate(-1)}>
                ← Back to the title
              </button>
              <TrailerButton trailer={label.trailer} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // The three menus' contents, drawn as popovers over the control row on a
  // desktop and as bottom sheets on a phone - the same rows either way.
  const qualityPanel = (
    <>
      <div className="cc-row">
        <span className="cc-title">Quality</span>
      </div>

      <div className="cc-group">
        {/* Auto is the top of the same ranking the play button
            followed, named rather than left implicit — otherwise
            there is no way back to "just pick for me". */}
        <button
          type="button"
          className={`cc-item${auto ? ' is-active' : ''}`}
          onClick={() => chooseTier(autoTier, true)}
          disabled={Boolean(switching)}
        >
          <span>Auto</span>
          <span className="cc-count">
            {switching === 'auto' ? '…' : autoTier.label}
          </span>
        </button>

        {tiers.map((tier) => (
          <button
            key={tier.key}
            type="button"
            className={`cc-item${
              !auto && tier.release.infoHash === activeHash ? ' is-active' : ''
            }`}
            onClick={() => chooseTier(tier)}
            disabled={Boolean(switching)}
          >
            <span>{tier.label}</span>
            {/* A resolution tag on a cam describes what it was
                scaled to, not what it looks like, so the row has
                to say which it is - otherwise "1080p" reads as a
                better picture than the 720p above it. */}
            {tier.release.cam && <span className="cc-tag cc-tag--warn">CAM</span>}
            {tier.release.cached === true && <span className="cc-tag">INSTANT</span>}
            {/* Why Auto passed this tier over. Without it, picking
                4K and watching it never start looks like a bug
                rather than a swarm that cannot carry the bitrate. */}
            {!tier.streamable && tier.release.cached !== true && (
              <span className="cc-tag cc-tag--warn">SLOW</span>
            )}
            <span className="cc-count">
              {switching === tier.key ? '…' : tier.release.size}
            </span>
          </button>
        ))}
      </div>

      {/* Switching is not free — it opens a different file, which
          has to buffer from wherever the film currently is. */}
      <p className="cc-note">Switching reopens the stream where you are.</p>
    </>
  );

  const audioPanel = (
    <>
      {label.title && (
        <>
          <div className="cc-row">
            <span className="cc-title">Language</span>
          </div>

          {langNote && <p className="cc-note cc-note--bad">{langNote}</p>}
          {/* A language search that found nothing in that
              language is playing an original-audio fallback;
              saying so beats a "French" row lit over English. */}
          {!langNote && audioFound?.wanted && audioFound.matched === 0 && (
            <p className="cc-note">
              Nothing dubbed in {audioLanguageLabel(audioFound.wanted)} was found, so this is the
              original audio.
            </p>
          )}

          <div className="cc-group">
            {[...langOptions.known, ...(langMore ? langOptions.rest : [])].map((lang) => (
              <button
                key={lang.code}
                type="button"
                className={`cc-item${playingLang === lang.code ? ' is-active' : ''}`}
                onClick={() => switchLanguage(lang.code)}
                disabled={Boolean(langSwitching)}
              >
                <span>
                  {lang.code === 'original' && label.originalLanguage
                    ? `Original (${languageOf(label.originalLanguage) || label.originalLanguage})`
                    : lang.label}
                </span>
                <span className="cc-count">
                  {langSwitching === lang.code ? 'finding…' : releasesIn.get(lang.code) || ''}
                </span>
              </button>
            ))}
            {!langMore && langOptions.rest.length > 0 && (
              <button type="button" className="cc-item" onClick={() => setLangMore(true)}>
                <span>More languages</span>
                <span className="cc-count">{langOptions.rest.length}</span>
              </button>
            )}
          </div>

          {/* Not free, and the menu should say so before the
              click rather than after a minute of buffering */}
          <p className="cc-note">
            A track already in the file switches instantly; anything else opens a dubbed release
            where you are.
          </p>
        </>
      )}

      {(audioTracks.length > 0 || audioNote) && (
        <div className="cc-row">
          <span className="cc-title">Tracks in this file</span>
        </div>
      )}

      {/* The button is here with nothing to offer precisely when
          the film is most likely to be silent, so this is the one
          place a viewer looking for the missing sound will land. */}
      {audioNote && (
        <p className="cc-note cc-note--bad">
          {audioNote}, so a soundtrack this browser cannot decode
          cannot be converted to one it can.
        </p>
      )}

      {/* Said plainly, because the alternative is a viewer who
          can hear sound but cannot tell why seeking got slower. */}
      {transcoding && (
        <p className="cc-note">
          {probe?.reencode
            ? 'Converting audio this browser cannot decode. Seeking restarts the stream.'
            : 'Playing a different track from the file. Seeking restarts the stream.'}
        </p>
      )}

      <div className="cc-group">
        {audioTracks.map((entry) => (
          <button
            key={entry.ordinal}
            type="button"
            className={`cc-item${entry.ordinal === activeAudio ? ' is-active' : ''}`}
            onClick={() => {
              setAudioOrdinal(entry.ordinal);
              // The new track starts from where we are, not from
              // the top - switching audio is not a restart.
              setBase(current);
              swapping.current = true;
            }}
          >
            <span>{audioLabel(entry)}</span>
            {/* The one fact that decides whether this costs the
                host anything, so it is on the row that chooses it */}
            {!entry.playable && <span className="cc-tag">CONV</span>}
          </button>
        ))}
      </div>
    </>
  );

  const ccPanel = (
    <>
      <div className="cc-row">
        <span className="cc-title">Captions</span>
        {selected && ccOn ? (
          <button type="button" className="cc-off" onClick={toggleCaptions}>
            Turn off
          </button>
        ) : null}
      </div>

      {!ccQuery && <p className="cc-note">This stream came without title information.</p>}
      {captions === 'loading' && <p className="cc-note">Looking for captions…</p>}
      {captions === 'error' && <p className="cc-note">Could not reach the subtitle sources.</p>}
      {captions === 'none' && <p className="cc-note">Nothing found for this title.</p>}
      {dead && ccOn && (
        <p className="cc-note cc-note--bad">That file would not load. Try another below.</p>
      )}

      {captions === 'ready' && (
        <>
          <div className="cc-group">
            {languages.map((entry) => (
              <button
                key={entry.code}
                type="button"
                className={`cc-item${
                  ccOn && selected?.language === entry.code ? ' is-active' : ''
                }`}
                onClick={() => chooseLanguage(entry.code)}
              >
                <span>{entry.name}</span>
                <span className="cc-count">{entry.count}</span>
              </button>
            ))}
          </div>

          {inLanguage.length > 1 && (
            <div className="cc-group cc-group--releases">
              {/* Ranking is a guess about which file matches this
                  particular rip; when it guesses wrong the fix is
                  trying the next one, so they are all reachable. */}
              {inLanguage.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  className={`cc-item${entry.id === trackId && ccOn ? ' is-active' : ''}`}
                  onClick={() => chooseTrack(entry)}
                  title={entry.release}
                >
                  <span className="cc-release">{entry.release || 'Untitled release'}</span>
                  {entry.hearingImpaired && <span className="cc-tag">HI</span>}
                  {/* Download count, not score: the ordering already
                      says what the score says, and two uploads can
                      share a release name - this is what tells them
                      apart and what says which one people kept. */}
                  <span className="cc-count">{downloads(entry.downloads)}</span>
                </button>
              ))}
            </div>
          )}

          <div className="cc-row cc-auto">
            <span className={`cc-auto-state cc-auto-state--${ccAuto.status}`}>{autoLabel(ccAuto)}</span>
            <button
              type="button"
              className="cc-auto-btn"
              onClick={() => setCcAutoRun((n) => n + 1)}
              disabled={ccAuto.status === 'running' || ccAuto.status === 'unavailable'}
            >
              {ccAuto.status === 'running' ? 'Syncing…' : 'Auto sync'}
            </button>
          </div>

          <div className="cc-row cc-sync">
            <span className="cc-title">Timing</span>
            <button
              type="button"
              className="cc-step"
              onClick={() => nudge(-SYNC_STEP)}
              aria-label="Captions earlier"
            >
              −
            </button>
            <span className="cc-offset">{syncLabel(offset + ccAuto.offset)}</span>
            <button
              type="button"
              className="cc-step"
              onClick={() => nudge(SYNC_STEP)}
              aria-label="Captions later"
            >
              +
            </button>
          </div>
        </>
      )}
    </>
  );

  // The season's episodes, for the phone's Episodes sheet. Picking one runs
  // the same search-and-start the Next episode button does.
  const episodesPanel = !season ? null : (
    <>
      {season.seasons?.length > 1 && (
        <div className="m-seasons">
          {season.seasons.map((number) => (
            <button
              key={number}
              type="button"
              className={`m-chip${number === season.number ? ' is-active' : ''}`}
              onClick={() => loadSeason(number)}
            >
              Season {number}
            </button>
          ))}
        </div>
      )}
      {nextError && <p className="cc-note cc-note--bad">{nextError}</p>}
      {nextBusy && <p className="cc-note">Finding the episode…</p>}
      {season.loading && <p className="cc-note">Loading episodes…</p>}
      {season.error && <p className="cc-note cc-note--bad">{season.error}</p>}
      <div className="m-episodes">
        {(season.episodes || []).map((entry) => {
          const here = season.number === label.season && entry.number === label.episode;
          return (
            <button
              key={entry.number}
              type="button"
              className={`m-ep${here ? ' is-active' : ''}`}
              disabled={here || nextBusy}
              onClick={() => {
                haptic(8);
                playEpisode({ season: season.number, episode: entry.number, name: entry.title, still: entry.still });
              }}
            >
              {entry.still ? <img src={entry.still} alt="" loading="lazy" /> : <span className="m-ep-blank" />}
              <span className="m-ep-text">
                <span className="m-ep-num">
                  E{entry.number}
                  {here ? ' · PLAYING' : ''}
                </span>
                <span className="m-ep-name">{entry.title || `Episode ${entry.number}`}</span>
              </span>
            </button>
          );
        })}
      </div>
    </>
  );

  /**
   * The phone's controls. Everything a thumb touches is at least 44 px; the
   * transport sits in the middle of the picture where both thumbs reach it;
   * the menus are a row of labelled buttons opening bottom sheets; nothing
   * depends on hovering. Hidden and shown by the gesture layer under it.
   */
  const touchChrome = (
    <div className="player-chrome m-chrome" onPointerDown={wake}>
      <div className="m-top">
        <button type="button" className="m-icon" onClick={() => navigate(-1)} aria-label="Close player">
          <Icon d={PATH.close} size={26} />
        </button>
        <div className="m-heading">
          <div className="player-heading">
            {label.title || 'Now playing'}
            {label.isTrailer && <span className="rs-tag player-tag">TRAILER</span>}
          </div>
          {subtitle && <div className="player-sub">{subtitle}</div>}
          {label.prerelease && (
            <div className="player-prerelease">{label.prerelease} — not the official home release</div>
          )}
        </div>
        {castTargets && mediaToken && (
          <button
            type="button"
            className={`m-icon${casting ? ' is-on' : ''}`}
            onClick={promptCast}
            aria-label={casting ? 'Playing on another screen' : 'Play on a TV'}
          >
            <Icon d={PATH.cast} size={22} />
          </button>
        )}
      </div>

      {!endCard && (
        <div className="m-center">
          <button type="button" className="m-skip" onClick={() => skip(-SKIP_SECONDS)} aria-label="Back 10 seconds">
            <Icon d={PATH.back10} size={30} />
          </button>
          <button type="button" className="m-play" onClick={togglePlay} aria-label={playing ? 'Pause' : 'Play'}>
            {busy ? <span className="player-ring" /> : <Icon d={playing ? PATH.pause : PATH.play} size={38} />}
          </button>
          <button type="button" className="m-skip" onClick={() => skip(SKIP_SECONDS)} aria-label="Forward 10 seconds">
            <Icon d={PATH.fwd10} size={30} />
          </button>
        </div>
      )}

      <div className="m-bottom">
        <div className="m-times">
          <span>{clock(current)}</span>
          <span>−{clock(Math.max(0, duration - current))}</span>
        </div>
        <div
          ref={trackRef}
          className={`scrub scrub--touch${busy ? ' is-loading' : ''}${scrubbing ? ' is-scrubbing' : ''}`}
          onPointerDown={onTrackDown}
          onPointerMove={onTrackMove}
          onPointerUp={onTrackUp}
          onPointerCancel={onTrackUp}
          role="slider"
          tabIndex={-1}
          aria-label="Seek"
          aria-valuemin={0}
          aria-valuemax={Math.round(duration)}
          aria-valuenow={Math.round(current)}
        >
          <div className="scrub-track">
            <div className="scrub-buffer" style={{ width: pct(buffered) }} />
            <div className="scrub-played" style={{ width: pct(current) }} />
          </div>
          <div className="scrub-knob" style={{ left: pct(current) }} />
          {scrubbing && hover && duration ? (
            <div className="m-preview" style={{ left: `clamp(84px, ${hover.x}px, calc(100% - 84px))` }}>
              {transcoding ? (
                stillSrc ? <img src={stillSrc} alt="" /> : <span className="m-preview-blank" />
              ) : (
                <canvas
                  ref={(element) => {
                    previewRef.current = element;
                    // The frame under the thumb before the first seek lands
                    if (element && !element.dataset.drawn) {
                      element.dataset.drawn = '1';
                      drawPreview();
                    }
                  }}
                  width="160"
                  height="90"
                />
              )}
              <span className="m-preview-time">{clock(hover.ratio * duration)}</span>
            </div>
          ) : null}
        </div>

        <div className="m-actions">
          {label.type === 'series' && seriesId && label.season != null && (
            <button type="button" className="m-act" onClick={() => toggleMenu('episodes')}>
              <Icon d={PATH.episodes} />
              <span>Episodes</span>
            </button>
          )}
          {(audioTracks.length > 0 || audioNote || label.title) && (
            <button
              type="button"
              className={`m-act${transcoding || playingLang !== 'original' ? ' is-on' : ''}`}
              onClick={() => toggleMenu('audio')}
            >
              <Icon d={PATH.audio} />
              <span>{playingLang !== 'original' ? playingLang.toUpperCase() : 'Audio'}</span>
            </button>
          )}
          <button
            type="button"
            className={`m-act${ccOn && selected && !dead ? ' is-on' : ''}`}
            onClick={() => toggleMenu('cc')}
          >
            <Icon d={PATH.cc} />
            <span>Subtitles</span>
          </button>
          {tiers.length > 0 && (
            <button type="button" className={`m-act${auto ? '' : ' is-on'}`} onClick={() => toggleMenu('quality')}>
              <Icon d={PATH.quality} />
              <span>{activeTier ? activeTier.label : 'Quality'}</span>
            </button>
          )}
          <button type="button" className="m-act" onClick={toggleFullscreen}>
            <Icon d={fullscreen ? PATH.exitFull : PATH.enterFull} />
            <span>{fullscreen ? 'Exit' : 'Full screen'}</span>
          </button>
        </div>
      </div>
    </div>
  );

  const sheets = (
    <>
      <Sheet open={menu === 'quality'} title="Quality" onClose={closeMenu}>
        {qualityPanel}
      </Sheet>
      <Sheet open={menu === 'audio'} title="Audio" onClose={closeMenu}>
        {audioPanel}
      </Sheet>
      <Sheet open={menu === 'cc'} title="Subtitles" onClose={closeMenu}>
        {ccPanel}
      </Sheet>
      <Sheet open={menu === 'episodes'} title="Episodes" onClose={closeMenu}>
        {episodesPanel}
      </Sheet>
    </>
  );

  return (
    <div
      ref={shellRef}
      className={`player player--stage${hideChrome ? ' is-idle' : ''}${touchUI ? ' player--touch' : ''}`}
      // Pointer rather than mouse events: a tap on a phone also fires a
      // synthetic mousemove, which would show the controls the tap just hid
      onPointerMove={(event) => event.pointerType === 'mouse' && wake()}
      onPointerLeave={(event) => event.pointerType === 'mouse' && setIdle(true)}
      style={pull ? { transform: `translateY(${pull}px)`, opacity: Math.max(0.4, 1 - pull / 500) } : undefined}
    >
      <video
        ref={videoRef}
        className="player-video"
        src={playSrc}
        autoPlay
        playsInline
        x-webkit-airplay="allow"
        onClick={togglePlay}
        onDoubleClick={toggleFullscreen}
        onLoadedMetadata={(event) => setMediaDuration(event.target.duration || 0)}
        onDurationChange={(event) => setMediaDuration(event.target.duration || 0)}
        onTimeUpdate={onTimeUpdate}
        onProgress={syncBuffered}
        onPlay={() => {
          setPlaying(true);
          setEnded(false);
          setAutoplayBlocked(false);
        }}
        onLoadedData={() => setStarted(true)}
        onPause={() => {
          setPlaying(false);
          report();
        }}
        onWaiting={() => setWaiting(true)}
        onPlaying={() => {
          swapping.current = false;
          // The stream from where a scrub let go is showing; the still can go
          setStillSrc(null);
          setWaiting(false);
          setStarted(true);
        }}
        onCanPlay={() => {
          swapping.current = false;
          setWaiting(false);
        }}
        onSeeking={() => setWaiting(true)}
        onSeeked={() => {
          setWaiting(false);
          // Mid-drag, the landing of one seek is the cue for the next
          seekInFlight.current = false;
          if (scrubbing && touchUI) drawPreview();
          if (scrubbing && !transcoding) sendSeek();
          // Scrubbing back out of the credits is a decision to keep watching,
          // and the end card has no business over the top of it
          setEnded(false);
        }}
        onVolumeChange={(event) => {
          setVolume(event.target.volume);
          setMuted(event.target.muted);
        }}
        onEnded={() => {
          report();
          if (swapping.current) return;
          // The end of a transcoded response is not the end of the film.
          //
          // When the source drops while the browser's buffer is full, ffmpeg
          // exits and this response is closed - cleanly, because as far as HTTP
          // is concerned it finished. The browser has no way to know the film
          // did not, so it plays out the buffer, several minutes of it, and then
          // reports the film as over: the play button comes back, and pressing
          // it restarts this segment from wherever it began rather than carrying
          // on. That is the "it stops and jumps back to the last time it
          // buffered" the household kept hitting.
          //
          // A real ending lands within a keyframe or so of the probed runtime.
          // Anything short of that is the stream having ended, not the film, and
          // is resumed exactly like a stream that died loudly.
          if (transcoding) {
            const video = videoRef.current;
            const at = base + (video?.currentTime || 0);
            if (duration && at < duration - ENDED_SLACK_S) {
              if (!resumeAfterStall()) setError(deadEnd());
              return;
            }
          }
          // Past that test the film really is over, so the end card - and the
          // countdown to the next episode - is the right thing to raise.
          setEnded(true);
        }}
        onError={() => {
          if (swapping.current) return;
          // A source the browser will not open is not the end of the film. The
          // same bytes remuxed into fMP4 usually play - an AVI holding perfectly
          // ordinary H.264 is the common case - and that path already exists for
          // the audio problem. So the first failure asks for it rather than
          // ending here, and only a failure with ffmpeg already in the chain, or
          // a probe reporting no ffmpeg at all, is final.
          if (!transcoding) {
            setDirectFailed(true);
            setWaiting(true);
            return;
          }

          // A transcoded stream that dies mid-film is a broken *response*, not
          // a broken film, so it is resumed rather than reported.
          if (!resumeAfterStall()) setError(deadEnd());
        }}
      >
        {ccOn && vttUrl ? (
          // Keyed so a track change remounts rather than re-pointing a element
          // that would keep serving the previous file's parsed cues
          <track
            key={vttUrl}
            ref={ccRef}
            kind="subtitles"
            src={vttUrl}
            srcLang={selected?.language || 'en'}
            label={selected?.languageName || 'Subtitles'}
            default
          />
        ) : null}
        Your browser does not support the video tag.
      </video>
      {stillSrc && transcoding && <img className="player-still" src={stillSrc} alt="" aria-hidden="true" />}

      {/* The phone's brightness gesture: a shade over the picture */}
      {dim < 1 && <div className="m-dim" style={{ opacity: 1 - dim }} aria-hidden="true" />}

      {/* The artwork stays up over the not-yet-started picture, so pressing
          Play never trades the film's cover for a black screen. Gone for good
          the moment a first frame exists — rebuffering mid-film is the
          picture's own business. */}
      {!started && cover}

      {/* Not over a phone's scrub preview, which sits where captions do */}
      {ccOn && cue && !(touchUI && scrubbing) ? (
        <div className="player-cc" aria-live="polite">
          {cue.split('\n').map((line, index) => (
            // Cue lines have no identity of their own, and a cue is replaced
            // wholesale rather than reordered
            <span key={index}>{line}</span>
          ))}
        </div>
      ) : null}

      {/* A failed quality switch left the film playing, so this is a note rather
          than the error screen — and it outlives the chrome fading out. */}
      {swapError && (
        <button type="button" className="player-toast alert" onClick={() => setSwapError('')}>
          {swapError}
        </button>
      )}

      {playingCam && !camAcknowledged && !swapError && (
        <button
          type="button"
          className="player-toast alert"
          onClick={() => setCamAcknowledged(true)}
        >
          {onlyCams
            ? 'This is a cam — filmed in a cinema. Every copy of this film so far is one; there is nothing better to switch to yet.'
            : 'This is a cam — filmed in a cinema. Better copies are in the quality menu.'}
        </button>
      )}

      {/* Over the picture and under every control: taps, double taps and
          swipes (see the touch section above) */}
      {touchUI && (
        <div
          className="m-gestures"
          onPointerDown={onGestureDown}
          onPointerMove={onGestureMove}
          onPointerUp={onGestureUp}
          onPointerCancel={onGestureUp}
          aria-hidden="true"
        />
      )}

      {seekBubble && (
        <div className={`m-seek m-seek--${seekBubble.side}`} aria-live="polite">
          <span
            key={seekBubble.key}
            className="m-ripple"
            // The forward half starts 60% across, which is 150% of its own
            // 40% width; the tap was measured from the player's left edge
            style={{
              left: seekBubble.side === 'fwd' ? `calc(${seekBubble.x}px - 150%)` : `${seekBubble.x}px`,
              top: `${seekBubble.y}px`
            }}
          />
          <div className="m-seek-label">
            <Icon d={seekBubble.side === 'back' ? PATH.back10 : PATH.fwd10} size={26} />
            <span>
              {seekBubble.side === 'back' ? '−' : '+'}
              {seekBubble.total}s
            </span>
          </div>
        </div>
      )}

      {hud && (
        <div className={`m-hud m-hud--${hud.kind}`} aria-hidden="true">
          <Icon d={hud.kind === 'volume' ? (hud.level === 0 ? PATH.muted : PATH.volume) : PATH.brightness} size={20} />
          <div className="m-hud-bar">
            <div className="m-hud-fill" style={{ height: `${Math.round(hud.level * 100)}%` }} />
          </div>
        </div>
      )}

      {/* On a phone the spinner sits in the play button while the controls
          are up, and floats on its own only while they are hidden */}
      {busy && (!touchUI || hideChrome) && <span className="player-ring player-ring--float" />}

      {flash && (
        <div className={`player-flash player-flash--${flash}`}>
          <Icon d={flash === 'back' ? PATH.back10 : PATH.fwd10} size={30} />
          <span>{SKIP_SECONDS}s</span>
        </div>
      )}

      {/* Not while scrubbing: the pause is the drag's, and the button would sit
          over the very frames being looked for */}
      {!touchUI && !playing && !busy && !endCard && !scrubbing && (
        <button type="button" className="player-resume" onClick={togglePlay} aria-label="Play">
          <Icon d={PATH.play} size={34} />
        </button>
      )}

      {/* Standing offer during the credits. It stays put while the chrome
          fades, because the moment it exists for is exactly the one where
          nobody is moving the mouse. */}
      {upNextSoon && (
        <button type="button" className="next-pill" onClick={startNext} disabled={nextBusy}>
          {nextBusy ? <span className="play-spin" /> : <span className="play-tri play-tri--sm" />}
          <span>Next episode</span>
        </button>
      )}

      {/* The end of an episode, made for the least attentive moment of the
          sitting: one big focused button, a countdown that acts on its own,
          and the two ways out said in words. */}
      {endCard && (
        <>
          <div className="next-shade" />
          <div className="next-card">
            <div className="next-kicker">UP NEXT</div>
            <div className="next-name">
              S{nextEp.season} E{nextEp.episode}
              {nextEp.name ? ` · ${nextEp.name}` : ''}
            </div>

            {nextEp.still && <img className="next-still" src={nextEp.still} alt="" />}

            {nextError && <div className="alert next-alert">{nextError}</div>}

            <div className="next-actions">
              {/* autoFocus so Enter, Space and a TV remote's d-pad all land
                  here without any pointing */}
              <button
                type="button"
                className="btn btn--play next-primary"
                onClick={startNext}
                disabled={nextBusy}
                autoFocus
              >
                {nextBusy ? <span className="play-spin" /> : <span className="play-tri" />}
                {nextBusy
                  ? 'Finding the episode…'
                  : countdown != null
                    ? `Playing in ${countdown}s`
                    : 'Play next episode'}
              </button>
              {countdown != null && !nextBusy && (
                <button type="button" className="btn btn--soft" onClick={cancelAutoNext}>
                  Cancel
                </button>
              )}
            </div>

            <div className="next-actions">
              {seriesId && (
                <button
                  type="button"
                  className="btn btn--soft btn--sm"
                  onClick={() => navigate(`/title/series/${seriesId}`)}
                >
                  All episodes
                </button>
              )}
              <button
                type="button"
                className="btn btn--soft btn--sm"
                onClick={() => {
                  setEnded(false);
                  seekAbsolute(0);
                  videoRef.current?.play()?.catch(() => {});
                }}
              >
                Watch again
              </button>
            </div>
          </div>
        </>
      )}

      {touchUI && touchChrome}
      {touchUI && sheets}

      {!touchUI && (
      <div className="player-chrome">
        <div className="player-top">
          <button type="button" className="btn btn--sm" onClick={() => navigate(-1)}>
            ← Back
          </button>
          <div>
            <div className="player-heading">
              {label.title || 'Now playing'}
              {/* Never let a trailer pass for the film */}
              {label.isTrailer && <span className="rs-tag player-tag">TRAILER</span>}
            </div>
            {subtitle && <div className="player-sub">{subtitle}</div>}
            {label.prerelease && (
              <div className="player-prerelease">
                {label.prerelease} — not the official home release; this may be a different film or a fake
              </div>
            )}
          </div>
        </div>


        <div className="player-bottom">
          <div
            ref={trackRef}
            className={`scrub${busy ? ' is-loading' : ''}${scrubbing ? ' is-scrubbing' : ''}`}
            onPointerDown={onTrackDown}
            onPointerMove={onTrackMove}
            onPointerUp={onTrackUp}
            onPointerCancel={onTrackUp}
            onPointerLeave={() => setHover(null)}
            role="slider"
            tabIndex={-1}
            aria-label="Seek"
            aria-valuemin={0}
            aria-valuemax={Math.round(duration)}
            aria-valuenow={Math.round(current)}
          >
            <div className="scrub-track">
              <div className="scrub-buffer" style={{ width: pct(buffered) }} />
              <div className="scrub-played" style={{ width: pct(current) }} />
            </div>
            <div className="scrub-knob" style={{ left: pct(current) }} />
            {hover && duration ? (
              <div className="scrub-tip" style={{ left: `${hover.x}px` }}>
                {clock(hover.ratio * duration)}
              </div>
            ) : null}
          </div>

          <div className="player-controls">
            <button type="button" className="pbtn" onClick={togglePlay} aria-label={playing ? 'Pause' : 'Play'}>
              <Icon d={playing ? PATH.pause : PATH.play} />
            </button>
            <button type="button" className="pbtn" onClick={() => skip(-SKIP_SECONDS)} aria-label="Back 10 seconds">
              <Icon d={PATH.back10} />
            </button>
            <button type="button" className="pbtn" onClick={() => skip(SKIP_SECONDS)} aria-label="Forward 10 seconds">
              <Icon d={PATH.fwd10} />
            </button>

            <div className="player-time">
              {clock(current)}
              <span className="player-time-sep">/</span>
              <span className="player-time-total">{clock(duration)}</span>
            </div>

            <div className="player-spacer" />

            <div className="player-vol">
              <button type="button" className="pbtn" onClick={toggleMute} aria-label={muted ? 'Unmute' : 'Mute'}>
                <Icon d={muted || volume === 0 ? PATH.muted : PATH.volume} />
              </button>
              <input
                type="range"
                className="vol-range"
                min="0"
                max="1"
                step="0.01"
                value={muted ? 0 : volume}
                onChange={(event) => changeVolume(Number(event.target.value))}
                aria-label="Volume"
              />
            </div>

            {tiers.length > 0 && (
              <div className="player-cc-wrap">
                <button
                  type="button"
                  className={`pbtn${activeTier ? ' pbtn--wide' : ''}${auto ? '' : ' is-on'}`}
                  onClick={() => toggleMenu('quality')}
                  aria-label="Quality"
                  aria-expanded={qualityMenu}
                >
                  <Icon d={PATH.quality} />
                  {activeTier && <span className="pbtn-tag">{activeTier.label}</span>}
                </button>

                {qualityMenu && !touchUI && <div className="cc-menu">{qualityPanel}</div>}
              </div>
            )}

            {(audioTracks.length > 0 || audioNote || label.title) && (
              <div className="player-cc-wrap">
                <button
                  type="button"
                  className={`pbtn${playingLang !== 'original' ? ' pbtn--wide' : ''}${
                    transcoding || playingLang !== 'original' ? ' is-on' : ''
                  }`}
                  onClick={() => toggleMenu('audio')}
                  aria-label="Audio and language"
                  aria-expanded={audioMenu}
                >
                  <Icon d={PATH.audio} />
                  {/* Said on the button, because a dubbed film is the one
                      state someone glancing at the controls should see */}
                  {playingLang !== 'original' && (
                    <span className="pbtn-tag">{playingLang.toUpperCase()}</span>
                  )}
                </button>

                {audioMenu && !touchUI && <div className="cc-menu">{audioPanel}</div>}
              </div>
            )}

            <div className="player-cc-wrap">
              <button
                type="button"
                className={`pbtn${ccOn && selected && !dead ? ' is-on' : ''}`}
                onClick={() => toggleMenu('cc')}
                aria-label="Captions"
                aria-expanded={ccMenu}
              >
                <Icon d={PATH.cc} />
              </button>

              {ccMenu && !touchUI && <div className="cc-menu">{ccPanel}</div>}
            </div>

            {/* Only with a target to send to AND a token to send with — a
                cast device fetches the URL cookie-less, so without the token
                the picker would connect and then 401. */}
            {castTargets && mediaToken && (
              <button
                type="button"
                className={`pbtn${casting ? ' is-on' : ''}`}
                onClick={promptCast}
                aria-label={casting ? 'Playing on another screen' : 'Play on a TV'}
              >
                <Icon d={PATH.cast} />
              </button>
            )}

            <button
              type="button"
              className="pbtn"
              onClick={toggleFullscreen}
              aria-label={fullscreen ? 'Exit full screen' : 'Full screen'}
            >
              <Icon d={fullscreen ? PATH.exitFull : PATH.enterFull} />
            </button>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default Player;
